import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import DashboardHeader from '../components/common/DashboardHeader';
import {
  Box,
  Drawer,
  Typography,
  TextField,
  Button,
  Radio,
  RadioGroup,
  FormControlLabel,
  IconButton,
  Paper,
  Grid,
  useTheme,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormGroup,
  ToggleButton,
  ToggleButtonGroup,
  InputAdornment,
  LinearProgress,
  Alert,
  Menu,
  ListItemIcon,
  ListItemText,
  Tabs,
  Tab,
  Card,
  CardContent,
  Avatar,
  Divider,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  FormHelperText,
  Switch,
  alpha,
  Skeleton,
  Snackbar,
  Collapse,
} from '@mui/material';
import DatePicker from '../shared/components/forms/HotelDatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import InventoryIcon from '@mui/icons-material/Inventory';
import { 
  Add, 
  ArrowDropDown as ArrowDropDownIcon,
  Block,
  CheckCircle,
  Cancel,
  AddCircle,
  RemoveCircle,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Remove as RemoveIcon,
  ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material';
import moment from 'moment';
import { getInventoryData, getInventoryForDate, saveInventory, setAvailabilityRange, setRestrictionsRange, getChannels, getRatePlans, getConfigurationRoomTypes } from '../services/api';
import ChannelSelect from '../components/common/ChannelSelect';
import ChannelImage from '../components/common/ChannelImage';
import StatusCellTooltip from '../components/common/StatusCellTooltip';
import { getInventoryCellTooltip } from '../utils/inventoryRateCellTooltips';
import {
  fetchActiveTodayInventoryBlocks,
  createInventoryBlock,
  updateInventoryBlock,
  deleteInventoryBlock,
  setInventoryBlockActive,
  getInventoryBlockErrorMessage,
  defaultInventoryBlockStartTime,
  defaultInventoryBlockEndTime,
  parseInventoryBlockTime,
  formatInventoryBlockTime,
} from '../services/inventoryBlockService';
import { detectThemeSync, watchThemeChanges } from '../utils/themeUtils';
import { getStaticFileUrl } from '../utils/staticUtils';
import { isMobileShellLayout } from '../utils/mobileShellLayout';
import {
  getDashboardPageShellSx,
  getDashboardPaperSx,
  getDashboardDarkSurfaceTokens,
} from '../utils/dashboardSurfaceSx';
import {
  inventoryVisibleDayCount,
  inventoryGridRowSx,
  inventoryGridScrollInnerSx,
  inventoryStickyLabelCellSx,
  inventoryDateHeaderCellSx,
  inventoryDateHeaderLabelSx,
  inventoryDateHeaderDaySx,
  inventoryMobileToolbarSx,
  inventoryMobileToolbarFilterRowSx,
  inventoryDataCellSx,
  inventoryStatusDotSx,
  inventoryChannelCornerCellSx,
  inventoryDrawerHeaderSx,
  inventoryDrawerFooterSx,
  inventoryMenuPaperSx,
  inventoryRateCellFillSx,
  isWeekendDate,
} from '../utils/inventoryMobileLayout';
import {
  MobileInventoryGridCorner,
  InventoryGridSkeleton,
} from '../components/inventory/MobileInventoryGridParts';
import InventoryRoomTypeLabel from '../components/inventory/InventoryRoomTypeLabel';
import RateActionSuccessDialog, { RATE_SUCCESS_COPY } from '../components/rates/RateActionSuccessDialog';
import EventBlockAdjustView from '../components/inventory/EventBlockAdjustView';
import AllChannelsSelector, {
  DEFAULT_RATE_CHANNEL_SELECTION,
  deriveBlockChannelSelection,
  expandChannelSelection,
} from '../components/common/AllChannelsSelector';
import {
  createDrawerOverlayProps,
  getDrawerRootSx,
} from '../utils/drawerOverlayProps';

const THEME_COLOR = '#4ce1b6';

const DEFAULT_RESTRICTION_FIELDS = {
  closeOnArrival: false,
  closeOnDeparture: false,
  minimumStay: '',
  maximumStay: '',
  minimumStayArrival: '',
  maximumStayArrival: '',
  exactStayArrival: '',
  minimumAdvanceReservation: '',
  maximumAdvanceReservation: '',
};

const createDefaultInventoryBlockWindow = () => {
  const start = moment().seconds(0).milliseconds(0);
  return {
    start_date: start.clone(),
    end_date: start.clone().add(1, 'day'),
    start_time: start.clone(),
    end_time: start.clone(),
  };
};

const ManageInventoryPage = () => {
  // Initialize theme synchronously to prevent flash
  const [currentTheme, setCurrentTheme] = useState(() => detectThemeSync());
  const isDark = currentTheme === 'dark';
  const themeColors = getDashboardDarkSurfaceTokens(isDark);
  const theme = useTheme(); // Use MUI theme for palette access
  const isMobileLayout = isMobileShellLayout();
  const drawerOverlays = useMemo(
    () => createDrawerOverlayProps(isDark, isDark ? themeColors.surface : '#ffffff'),
    [isDark, themeColors.surface]
  );

  // Watch for theme changes
  useEffect(() => {
    const cleanup = watchThemeChanges((newTheme) => {
      setCurrentTheme(newTheme);
    });

    return cleanup;
  }, []);
  const [listings, setListings] = useState([]);
  const [allRoomTypes, setAllRoomTypes] = useState([]); // All available room types for dropdown
  const [selectedDates, setSelectedDates] = useState([]);
  const [selectedRoomType, setSelectedRoomType] = useState(null);
  const [selectedRoomTypes, setSelectedRoomTypes] = useState([]); // Multi-select in Update Inventory drawer
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [calendarStartDate, setCalendarStartDate] = useState(moment()); // Selected date for calendar start (default: current date)
  const [dateOffset, setDateOffset] = useState(0); // Days offset from today (calculated from calendarStartDate)
  const [selectedRoomTypeFilter, setSelectedRoomTypeFilter] = useState('all'); // Filter by room type ('all' or room type ID)
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true); // Track initial load to prevent showing red before data loads
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false); // Track if we've loaded data at least once
  const [isInventoryGridLoading, setIsInventoryGridLoading] = useState(true); // Skeleton only in grid, not channel/date section
  const cachedInventoryRef = useRef({}); // Cache inventory by date range using ref to avoid dependency issues
  const [channels, setChannels] = useState([]);
  const [selectedChannel, setSelectedChannel] = useState('all'); // Selected channel filter (default: All Channels)
  const [ratePlans, setRatePlans] = useState([]); // Available rate plans (meal plans)
  const [selectedRatePlan, setSelectedRatePlan] = useState(null); // Selected meal plan
  const [allChannelsInventory, setAllChannelsInventory] = useState({}); // Inventory for all channels when "All Channels" is selected
  const [isSaving, setIsSaving] = useState(false); // Track saving state
  const [errorMessage, setErrorMessage] = useState(null); // Track error messages
  const [successDialog, setSuccessDialog] = useState(null);
  const [snackbarMessage, setSnackbarMessage] = useState(null); // For messages when drawer is closed (e.g. past date clicked)
  const [fullView, setFullView] = useState(false); // Full view toggle - shows all channels with all room types
  const [channelView, setChannelView] = useState(false); // ON: per-OTA rows; OFF: PMS + All Channels (both expanded)
  const [expandedChannels, setExpandedChannels] = useState({ pms: false, combined: true }); // PMS closed; All Channels open by default
  
  // State for Add New dropdown menu
  const [addMenuAnchor, setAddMenuAnchor] = useState(null);
  
  // State for Inventory +/- mode
  const [inventoryAdjustMode, setInventoryAdjustMode] = useState(null); // 'add' or 'delete'
  const [inventoryBlocks, setInventoryBlocks] = useState([]);
  const [inventoryBlockLoading, setInventoryBlockLoading] = useState(false);
  const [editingInventoryBlock, setEditingInventoryBlock] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [blockToDelete, setBlockToDelete] = useState(null);
  const drawerContainerRef = useRef(null);
  const [inventoryBlockFormData, setInventoryBlockFormData] = useState({
    room_type_id: '',
    block_type: 'MANUAL',
    ...createDefaultInventoryBlockWindow(),
    num_rooms: 1,
    reason: '',
    is_active: true,
    selectedChannels: [...DEFAULT_RATE_CHANNEL_SELECTION],
    selected_room_ids: [],
  });
  const [inventoryBlockErrors, setInventoryBlockErrors] = useState({});
  
  // Selected date details state
  const [selectedInventory, setSelectedInventory] = useState({
    available: 'available', // 'available' | 'restrictions' | 'sold_out'
    block_type: 'master',
    restriction_action: 'apply', // 'apply' | 'clear'
    stop_sell_action: 'apply', // 'apply' | 'clear' (Stop Sell tab)
    restrictions: { ...DEFAULT_RESTRICTION_FIELDS },
    reason: '',
  });
  
  // Channel update messages
  const [channelUpdateMessages, setChannelUpdateMessages] = useState({});
  
  // Date range for drawer
  const [dateRange, setDateRange] = useState([moment().add(1, 'day'), moment().add(2, 'day')]);
  
  // Day selection
  const [daySelectionType, setDaySelectionType] = useState('custom'); // 'weekdays', 'weekends', 'all', 'custom'
  const [selectedDays, setSelectedDays] = useState([0, 1, 2, 3, 4, 5, 6]); // 0=Sunday, 1=Monday, ..., 6=Saturday
  const [selectedChannels, setSelectedChannels] = useState([...DEFAULT_RATE_CHANNEL_SELECTION]); // Selected channels for bulk update
  const hasChannels = Array.isArray(channels) && channels.length > 0;

  const closeUpdateInventoryDrawer = useCallback(() => {
    setSuccessDialog(null);
    setDrawerOpen(false);
    setSelectedDates([]);
    setChannelUpdateMessages({});
    setInventoryAdjustMode(null);
    setEditingInventoryBlock(null);
    setInventoryBlockFormData({
      room_type_id: '',
      block_type: 'MANUAL',
      ...createDefaultInventoryBlockWindow(),
      num_rooms: 1,
      reason: '',
      is_active: true,
      selectedChannels: hasChannels ? [...DEFAULT_RATE_CHANNEL_SELECTION] : ['pms'],
      selected_room_ids: [],
    });
    setInventoryBlockErrors({});
  }, [hasChannels]);

  // Constants
  const visibleDatesCount = inventoryVisibleDayCount(isMobileLayout);
  const DATE_BUFFER = 0; // Fetch only visible calendar dates
  
  // Helper function to get date range for API calls
  const getDateRange = useCallback((offset, buffer = DATE_BUFFER) => {
    const baseDate = moment().startOf('day');
    const start = baseDate.clone().add(offset - buffer, 'days').format('YYYY-MM-DD');
    const end = baseDate
      .clone()
      .add(offset + (visibleDatesCount - 1) + buffer, 'days')
      .format('YYYY-MM-DD');
    return { start, end };
  }, [visibleDatesCount]);
  
  // Calculate dateOffset from calendarStartDate
  React.useEffect(() => {
    const offset = calendarStartDate.clone().startOf('day').diff(moment().startOf('day'), 'days');
    setDateOffset(offset);
  }, [calendarStartDate]);

  // Generate dates for the calendar view (visible dates from selected start date)
  const dates = React.useMemo(() => {
    const dates = [];
    const start = calendarStartDate.clone();
    const end = calendarStartDate.clone().add(visibleDatesCount - 1, 'days');
    let current = start.clone();
    
    while (current.isSameOrBefore(end, 'day')) {
      dates.push(current.clone());
      current.add(1, 'day');
    }
    
    return dates;
  }, [calendarStartDate, visibleDatesCount]);

  // Fetch inventory data for visible dates plus buffer for smooth navigation
  const fetchInventory = useCallback(async (offset = dateOffset, showLoading = true, channelId = selectedChannel) => {
    // If "all" channels, don't fetch here - handled by separate useEffect
    if (channelId === 'all') {
      return { success: false, reason: 'all_channels' };
    }
    
    const cacheKey = `${offset}-${offset + visibleDatesCount}-${channelId}`;
    
    // Check cache first (only if showLoading is true, otherwise bypass cache to force refresh)
    if (showLoading && cachedInventoryRef.current[cacheKey]) {
      setListings(cachedInventoryRef.current[cacheKey]);
      // Always update loading states, even if showLoading is false
      setIsInitialLoad(false);
      setHasLoadedOnce(true);
      return { success: true, cached: true };
    }
    
    if (showLoading) {
      setLoading(true);
      setIsInitialLoad(true);
    }
    // When showLoading is false (force refresh after save), don't set isInitialLoad
    // This keeps existing data visible while new data is fetched, creating a smoother transition
    
    try {
      const { start, end } = getDateRange(offset);
      // If showLoading is false, it means we're force refreshing after save - bypass cache
      const data = await getInventoryData(start, end, channelId, !showLoading);
      
      if (data && data.listings) {
        // Ensure listings is an array
        const listingsArray = Array.isArray(data.listings) ? data.listings : [];
        cachedInventoryRef.current[cacheKey] = listingsArray;
        setListings(listingsArray);
        
        // Keep room types aligned with latest backend response to avoid stale entries
        // from previously visible data (e.g. after floor disable).
        if (listingsArray.length > 0) {
          const normalizedListings = listingsArray.map(rt => ({
            id: rt.id,
            name: rt.name || `Room Type ${rt.id}`,
            internal_name: rt.internal_name,
          }));
          setAllRoomTypes(normalizedListings);
        } else {
          setAllRoomTypes([]);
        }
        
        // Always update loading states after successful fetch, even if showLoading is false
        setIsInitialLoad(false);
        setHasLoadedOnce(true);
        return { success: true, listings: listingsArray };
      }
      
      // Even if no data, mark as loaded and set empty array
      setListings([]);
      setIsInitialLoad(false);
      setHasLoadedOnce(true);
      return { success: false, reason: 'no_data' };
    } catch (error) {
      // Always update loading states on error, even if showLoading is false
      setIsInitialLoad(false);
      setHasLoadedOnce(true);
      return { success: false, reason: 'error', error };
    } finally {
      if (showLoading) {
        setLoading(false);
      }
    }
  }, [dateOffset, selectedChannel, getDateRange]);

  // Initial load: Fetch channels, then load inventory (defer grid loading state so page shell paints first — smooth navigation)
  useEffect(() => {
    const loadChannels = async () => {
      try {
        const channelsData = await getChannels();
        const channelsList = channelsData?.channels || [];
        setChannels(channelsList);
        if (channelsList.length === 0 && selectedChannel === 'all') {
          setSelectedChannel('pms');
          setChannelView(false);
          setFullView(false);
          await fetchInventory(dateOffset, false, 'pms');
        } else if (selectedChannel === 'all') {
          setIsInventoryGridLoading(true);
          await fetchAllChannelInventory(dateOffset, channelsList, false, false);
          setIsInventoryGridLoading(false);
        }
      } catch (error) {
        setIsInventoryGridLoading(false);
      }
    };

    loadChannels();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run on mount

  // Helper: fetch all channel inventory in one API call (cached by date range)
  const fetchAllChannelInventory = useCallback(async (offset = dateOffset, _channelsList = null, bypassCache = false, showGlobalLoading = true) => {
    const cacheKey = `${offset}-${offset + visibleDatesCount}-all`;
    if (!bypassCache && cachedInventoryRef.current[cacheKey]) {
      const cached = cachedInventoryRef.current[cacheKey];
      const channelListings = cached?.channel_listings || cached || {};
      const pmsListings = channelListings.pms || cached?.listings || [];
      setAllChannelsInventory(channelListings);
      setListings(Array.isArray(pmsListings) ? pmsListings : []);
      const normalized = Array.isArray(pmsListings)
        ? pmsListings.map((rt) => ({
            id: rt.id,
            name: rt.name || `Room Type ${rt.id}`,
            internal_name: rt.internal_name,
          }))
        : [];
      if (normalized.length > 0) setAllRoomTypes(normalized);
      setIsInitialLoad(false);
      setHasLoadedOnce(true);
      return { success: true, cached: true };
    }

    if (showGlobalLoading) {
      setLoading(true);
      setIsInitialLoad(true);
    }

    try {
      const { start, end } = getDateRange(offset);
      const data = await getInventoryData(start, end, 'all', bypassCache);
      const channelListings = data?.channel_listings || {};
      const pmsListings = channelListings.pms || data?.listings || [];
      cachedInventoryRef.current[cacheKey] = data;
      setAllChannelsInventory(channelListings);
      setListings(pmsListings);

      const normalized = Array.isArray(pmsListings)
        ? pmsListings.map((rt) => ({
            id: rt.id,
            name: rt.name || `Room Type ${rt.id}`,
            internal_name: rt.internal_name,
          }))
        : [];
      setAllRoomTypes(normalized);

      setIsInitialLoad(false);
      setHasLoadedOnce(true);
      return { success: true, cached: false };
    } catch (error) {
      setAllChannelsInventory({});
      setListings([]);
      setIsInitialLoad(false);
      setHasLoadedOnce(true);
      return { success: false, error };
    } finally {
      if (showGlobalLoading) setLoading(false);
    }
  }, [dateOffset, getDateRange, visibleDatesCount]);

  // Load inventory when channel or date offset changes (skip initial mount to avoid duplicate)
  const isInitialMount = React.useRef(true);

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    let cancelled = false;
    const hasExistingData =
      selectedChannel === 'all'
        ? Object.keys(allChannelsInventory).length > 0
        : listings.length > 0;
    // Full skeleton only on cold load; otherwise keep stale grid (stale-while-revalidate)
    if (!hasExistingData) {
      setIsInventoryGridLoading(true);
      setIsInitialLoad(true);
    } else {
      setIsTransitioning(true);
    }

    const loadChannelInventory = async () => {
      try {
        if (selectedChannel === 'all') {
          await fetchAllChannelInventory(dateOffset, channels, false, false);
        } else {
          await fetchInventory(dateOffset, false, selectedChannel);
        }
      } catch (error) {
        if (!cancelled) {
          setIsInitialLoad(false);
          setHasLoadedOnce(true);
        }
      } finally {
        if (!cancelled) {
          setIsInventoryGridLoading(false);
          setIsInitialLoad(false);
          setHasLoadedOnce(true);
          setIsTransitioning(false);
        }
      }
    };

    loadChannelInventory();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedChannel, dateOffset]);

  // "all" is the default, no need to change it


  // Handle opening drawer with specific option selected
  const handleOpenDrawerWithOption = (option) => {
    // Use visible calendar range for all operations so ChannelHub gets full range by default
    let fromDate, toDate;
    if (dates && dates.length > 0) {
      fromDate = dates[0].clone();
      toDate = dates[dates.length - 1].clone();
    } else {
      fromDate = moment().add(1, 'day');
      toDate = moment().add(2, 'day');
    }

    setDateRange([fromDate, toDate]);
    
    // Set selectedDates to match dateRange
    setSelectedDates([{ 
      start: fromDate.format('YYYY-MM-DD'), 
      end: toDate.format('YYYY-MM-DD') 
    }]);
    
    // Set selected inventory option
    setSelectedInventory({
      available: option,
      block_type: 'master',
      restriction_action: 'apply',
      stop_sell_action: 'apply',
      restrictions: { ...DEFAULT_RESTRICTION_FIELDS },
      reason: '',
    });
    setChannelUpdateMessages({});
    
    // Default: use OTA channels only when at least one channel is configured.
    if (option === 'restrictions' || option === 'sold_out') {
      setSelectedChannels(hasChannels ? ['all'] : []);
    } else {
      setSelectedChannels(hasChannels ? [...DEFAULT_RATE_CHANNEL_SELECTION] : ['pms']);
    }
    
    // Room types: keep filter selection, or Select All when grid filter is All Room Types
    if (selectedRoomTypeFilter && selectedRoomTypeFilter !== 'all') {
      setSelectedRoomType(selectedRoomTypeFilter);
      setSelectedRoomTypes([selectedRoomTypeFilter]);
    } else if (allRoomTypes.length > 0) {
      const allIds = allRoomTypes.map((rt) => rt.id.toString());
      setSelectedRoomTypes(allIds);
      setSelectedRoomType(allIds[0] || null);
    } else if (listings.length > 0) {
      const allIds = listings.map((rt) => rt.id.toString());
      setSelectedRoomTypes(allIds);
      setSelectedRoomType(allIds[0] || null);
    }
    
    // Reset day selection
    setDaySelectionType('custom');
    setSelectedDays([0, 1, 2, 3, 4, 5, 6]);
    
    // Reset inventory adjust mode
    setInventoryAdjustMode(null);
    
    // Open drawer
    setSuccessDialog(null);
    setDrawerOpen(true);
  };

  // Handle opening inventory adjust drawer
  const handleOpenInventoryAdjust = async (mode) => {
    setInventoryAdjustMode(mode);
    
    if (mode !== 'delete') {
      const defaultWindow = createDefaultInventoryBlockWindow();
      // Default to first room type if available
      const defaultRoomTypeId = allRoomTypes.length > 0 ? allRoomTypes[0].id : '';
      
      setInventoryBlockFormData({
        room_type_id: defaultRoomTypeId,
        block_type: 'MANUAL',
        ...defaultWindow,
        num_rooms: 1,
        reason: '',
        is_active: true,
        selectedChannels: hasChannels ? [...DEFAULT_RATE_CHANNEL_SELECTION] : ['pms'],
        selected_room_ids: [],
      });
      setEditingInventoryBlock(null);
      setInventoryBlockErrors({});
    } else {
      // Ensure skeletons show while EventBlockAdjustView triggers onRefresh
      setInventoryBlockLoading(true);
    }
    
    // Open drawer immediately (Manage Event loads via child onRefresh)
    setSuccessDialog(null);
    setDrawerOpen(true);
  };

  // Fetch inventory blocks active today (Manage Event)
  const fetchInventoryBlocks = async () => {
    setInventoryBlockLoading(true);
    try {
      const data = await fetchActiveTodayInventoryBlocks();
      setInventoryBlocks(data.blocks || []);
    } catch (error) {setErrorMessage('Failed to load inventory blocks. Please try again.');
    } finally {
      setInventoryBlockLoading(false);
    }
  };

  // Handle date cell click
  const handleDateClick = async (roomTypeId, date, channelId = null) => {
    const clickedDate = moment(date);
    if (clickedDate.isBefore(moment().startOf('day'), 'day')) {
      setSnackbarMessage('Cannot set inventory for past dates');
      return;
    }
    try {
      // If "all" is selected, use the first room type for the drawer
      const actualRoomTypeId = roomTypeId === 'all' && listings.length > 0 
        ? listings[0].id.toString() 
        : roomTypeId;
      
      // Combined OTA row passes channelId "all"; fetch a concrete OTA for drawer seed data
      const requestedChannelId = channelId || selectedChannel;
      const fetchChannelId =
        requestedChannelId === 'all' && Array.isArray(channels) && channels[0]
          ? String(channels[0].id)
          : requestedChannelId;
      const response = await getInventoryForDate(date, actualRoomTypeId, fetchChannelId);
      const inventoryData = response.inventory || {};
      
      setSelectedRoomType(actualRoomTypeId);
      setSelectedRoomTypes([actualRoomTypeId]);
      const clickedChannelId = requestedChannelId;
      setSelectedDates([{
        start: clickedDate.format('YYYY-MM-DD'),
        end: clickedDate.format('YYYY-MM-DD'),
        roomTypeId: actualRoomTypeId,
        channelId: clickedChannelId === 'pms' ? 'pms' : clickedChannelId.toString()
      }]);
      // Use visible calendar range; clamp to today so we never set inventory for past dates
      const today = moment().startOf('day');
      let rangeStart = dates && dates.length > 0 ? dates[0].clone() : moment(date).clone();
      let rangeEnd = dates && dates.length > 0 ? dates[dates.length - 1].clone() : moment(date).clone().add(1, 'day');
      if (rangeStart.isBefore(today, 'day')) rangeStart = today.clone();
      if (rangeEnd.isBefore(rangeStart, 'day')) rangeEnd = rangeStart.clone().add(1, 'day');
      setDateRange([rangeStart, rangeEnd]);
      
      // Priority: Blocked → Stop Sell (sold_out) → Available
      let availabilityStatus = 'available';
      let restrictionAction = 'apply';
      if (inventoryData.available === false) {
        availabilityStatus = 'restrictions';
        restrictionAction = 'apply';
      } else if (inventoryData.sold_out === true) {
        availabilityStatus = 'sold_out';
      } else {
        availabilityStatus = 'available';
      }

      // Restrictions / Stop Sell are OTA-only; Available includes PMS + All Channels
      if (availabilityStatus === 'available') {
        setSelectedChannels(hasChannels ? [...DEFAULT_RATE_CHANNEL_SELECTION] : ['pms']);
      } else {
        setSelectedChannels(hasChannels ? ['all'] : []);
      }
      
      setSelectedInventory({
        available: availabilityStatus,
        block_type: inventoryData.block_type || 'master',
        restriction_action: restrictionAction,
        stop_sell_action: availabilityStatus === 'sold_out' ? 'clear' : 'apply',
        restrictions: { ...DEFAULT_RESTRICTION_FIELDS },
        reason: inventoryData.reason || '',
      });
      
      setSuccessDialog(null);
      setDrawerOpen(true);
    } catch (error) {
      // Error loading inventory for date - silently fail
    }
  };

  // Handle save inventory block
  const handleSaveInventoryBlock = async () => {
    const newErrors = {};
    if (!inventoryBlockFormData.room_type_id) {
      newErrors.room_type_id = 'Room type is required';
    }
    if (!inventoryBlockFormData.start_date) {
      newErrors.start_date = 'Start date is required';
    }
    if (!inventoryBlockFormData.end_date) {
      newErrors.end_date = 'End date is required';
    }
    if (inventoryBlockFormData.start_date && inventoryBlockFormData.end_date && inventoryBlockFormData.end_date.isBefore(inventoryBlockFormData.start_date, 'day')) {
      newErrors.end_date = 'End date must be after or equal to start date';
    }
    if (inventoryBlockFormData.start_date && inventoryBlockFormData.end_date && inventoryBlockFormData.end_date.isAfter(inventoryBlockFormData.start_date.clone().add(1, 'year'))) {
      newErrors.end_date = 'Date range cannot exceed 1 year';
    }
    if (inventoryBlockFormData.start_date && inventoryBlockFormData.start_date.isBefore(moment().startOf('day'), 'day')) {
      newErrors.start_date = 'Cannot set inventory for past dates';
    }
    const blockStartTime = parseInventoryBlockTime(inventoryBlockFormData.start_time);
    const blockEndTime = parseInventoryBlockTime(inventoryBlockFormData.end_time);
    if (!blockStartTime) {
      newErrors.start_time = 'Block time is required';
    }
    if (!blockEndTime) {
      newErrors.end_time = 'Unblock time is required';
    }
    if (
      blockStartTime &&
      blockEndTime &&
      inventoryBlockFormData.start_date &&
      inventoryBlockFormData.end_date &&
      inventoryBlockFormData.end_date.isSame(inventoryBlockFormData.start_date, 'day') &&
      !blockEndTime.isAfter(blockStartTime)
    ) {
      newErrors.end_time = 'Unblock time must be after block time on the same day';
    }
    if (!inventoryBlockFormData.selectedChannels || inventoryBlockFormData.selectedChannels.length === 0) {
      newErrors.selectedChannels = 'At least one channel must be selected';
    }
    const selectedRoomIds = Array.isArray(inventoryBlockFormData.selected_room_ids)
      ? inventoryBlockFormData.selected_room_ids.map((id) => Number(id)).filter(Boolean)
      : [];
    if (selectedRoomIds.length === 0) {
      newErrors.selected_room_ids = 'Select at least one room to block';
    }

    if (Object.keys(newErrors).length > 0) {
      setInventoryBlockErrors(newErrors);
      return;
    }

    setIsSaving(true);
    setErrorMessage(null);
    try {
      // Convert selectedChannels to channel_ids — expand All Channels to every OTA
      const expanded = expandChannelSelection(
        inventoryBlockFormData.selectedChannels || [],
        channels
      );
      const channelIds = expanded
        .filter((id) => id !== 'pms' && id !== 'all')
        .map((id) => parseInt(id, 10))
        .filter((id) => !Number.isNaN(id));
      
      const dataToSend = {
        room_type_id: inventoryBlockFormData.room_type_id,
        block_type: inventoryBlockFormData.block_type,
        start_date: inventoryBlockFormData.start_date ? inventoryBlockFormData.start_date.format('YYYY-MM-DD') : null,
        end_date: inventoryBlockFormData.end_date ? inventoryBlockFormData.end_date.format('YYYY-MM-DD') : null,
        start_time: formatInventoryBlockTime(inventoryBlockFormData.start_time),
        end_time: formatInventoryBlockTime(inventoryBlockFormData.end_time),
        num_rooms: selectedRoomIds.length,
        reason: inventoryBlockFormData.reason || null,
        is_active: inventoryBlockFormData.is_active,
        channel_ids: channelIds,
        include_pms: (inventoryBlockFormData.selectedChannels || []).includes('pms'),
        room_ids: selectedRoomIds,
      };

      const wasEditing = !!editingInventoryBlock;
      const wasDeleteMode = inventoryAdjustMode === 'delete';
      
      if (editingInventoryBlock) {
        await updateInventoryBlock(editingInventoryBlock.id, dataToSend);
        setErrorMessage(null);
      } else {
        await createInventoryBlock(dataToSend);
        setErrorMessage(null);
      }
      
      setSuccessDialog(
        wasEditing ? RATE_SUCCESS_COPY.inventoryBlockUpdate : RATE_SUCCESS_COPY.inventoryBlockCreate
      );

      // Reload inventory after creating/updating event block
      try {
        if (selectedChannel === 'all') {
          await fetchAllChannelInventory(dateOffset, channels, true);
        } else {
          await fetchInventory(dateOffset, false, selectedChannel);
        }
      } catch (error) {}
      
      // Refresh inventory blocks if in delete mode
      if (wasDeleteMode) {
        await fetchInventoryBlocks();
      }
    } catch (error) {if (error.response?.data?.errors) {
        setInventoryBlockErrors(error.response.data.errors);
      } else {
        setErrorMessage(error.response?.data?.error || 'Failed to save inventory block');
      }
    } finally {
      setIsSaving(false);
    }
  };

  // Handle delete inventory block
  const handleDeleteInventoryBlock = async () => {
    if (!blockToDelete) return;

    setIsSaving(true);
    try {
      await deleteInventoryBlock(blockToDelete.id);
      setDeleteDialogOpen(false);
      setBlockToDelete(null);
      await fetchInventoryBlocks();
      setErrorMessage(null);
      
      // Reload inventory after deleting event block
      try {
        if (selectedChannel === 'all') {
          await fetchAllChannelInventory(dateOffset, channels, true);
        } else {
          await fetchInventory(dateOffset, false, selectedChannel);
        }
      } catch (error) {}
    } catch (error) {setErrorMessage(error.response?.data?.error || 'Failed to delete inventory block');
    } finally {
      setIsSaving(false);
    }
  };

  const handleToggleInventoryBlockActive = async (block) => {
    if (!block?.id) return;
    setIsSaving(true);
    setErrorMessage(null);
    try {
      await setInventoryBlockActive(block, !block.is_active);
      await fetchInventoryBlocks();
      try {
        if (selectedChannel === 'all') {
          await fetchAllChannelInventory(dateOffset, channels, true);
        } else {
          await fetchInventory(dateOffset, false, selectedChannel);
        }
      } catch (error) {}
    } catch (error) {
      setErrorMessage(getInventoryBlockErrorMessage(error, 'Failed to update inventory block status'));
    } finally {
      setIsSaving(false);
    }
  };

  // Handle save inventory
  const handleSaveInventory = async () => {
    if (!selectedRoomTypes.length || selectedDates.length === 0) {
      setErrorMessage('Please select at least one room type and date');
      return;
    }
    
    setErrorMessage(null);
    setIsSaving(true);
    setLoading(true);
    try {
      // Visible calendar range (from useMemo 'dates') so we can fall back to full range when selection is single day
      const visibleStart = dates && dates.length > 0 ? dates[0].clone() : null;
      const visibleEnd = dates && dates.length > 0 ? dates[dates.length - 1].clone() : null;

      const datesToSend = [];
      // Use drawer's From/To (dateRange) when available so the range we send matches what's shown
      let rangeSource =
        dateRange && dateRange[0] && dateRange[1] && moment(dateRange[1]).isSameOrAfter(dateRange[0], 'day')
          ? { start: moment(dateRange[0]), end: moment(dateRange[1]) }
          : null;
      // If only a single day is selected, use full visible calendar range so "available" applies to date range not just selected day
      if (rangeSource && rangeSource.start.isSame(rangeSource.end, 'day') && visibleStart && visibleEnd && !visibleStart.isSame(visibleEnd, 'day')) {
        rangeSource = { start: visibleStart, end: visibleEnd };
      }
      const rangesToIterate = rangeSource
        ? [rangeSource]
        : selectedDates.map((dr) => ({ start: moment(dr.start), end: moment(dr.end) }));
      rangesToIterate.forEach(({ start, end }) => {
        let current = start.clone();
        while (current.isSameOrBefore(end, 'day')) {
          const dayOfWeek = current.day();
          let shouldInclude = false;
          if (daySelectionType === 'weekdays') {
            shouldInclude = dayOfWeek >= 1 && dayOfWeek <= 5;
          } else if (daySelectionType === 'weekends') {
            shouldInclude = dayOfWeek === 0 || dayOfWeek === 6;
          } else if (daySelectionType === 'custom') {
            shouldInclude = selectedDays.includes(dayOfWeek);
          }
          if (shouldInclude) {
            datesToSend.push(current.format('YYYY-MM-DD'));
          }
          current.add(1, 'day');
        }
      });

      if (datesToSend.length === 0) {
        setErrorMessage('No dates in range. Adjust day selection or date range.');
        setIsSaving(false);
        setLoading(false);
        return;
      }
      const todayStr = moment().startOf('day').format('YYYY-MM-DD');
      const hasPastDate = datesToSend.some((d) => d < todayStr);
      if (hasPastDate) {
        setErrorMessage('Cannot set inventory for past dates');
        setIsSaving(false);
        setLoading(false);
        return;
      }

      const start_date = datesToSend[0];
      const end_date = datesToSend[datesToSend.length - 1];
      const operation = selectedInventory.available;
      // Restrictions / Stop Sell are OTA-only (toChannels); Available can also target PMS
      const otaOnly = operation === 'restrictions' || operation === 'sold_out';
      const channelsToSave = otaOnly
        ? selectedChannels.filter((id) => id !== 'pms')
        : selectedChannels.length > 0
          ? selectedChannels
          : ['pms'];
      const pmsSelected = !otaOnly && channelsToSave.includes('pms');
      const allChannelsSelected = channelsToSave.includes('all');
      const specificOtaIds = channelsToSave.filter(
        (id) => id !== 'pms' && id !== 'all'
      );
      const hasOtaTargets = allChannelsSelected || specificOtaIds.length > 0;

      if (otaOnly && !hasOtaTargets) {
        setErrorMessage('Select All Channels or at least one OTA');
        setIsSaving(false);
        setLoading(false);
        return;
      }

      const stopSellApply = (selectedInventory.stop_sell_action || 'apply') === 'apply';
      const operationMessages = {
        sold_out: stopSellApply ? 'Applying stop sell...' : 'Clearing stop sell...',
        restrictions: 'Updating restrictions...',
        available: 'Syncing available inventory to all channels...',
      };
      const messages = {};
      if (pmsSelected) messages.pms = operationMessages[operation] || 'Updating...';
      if (hasOtaTargets) messages.all = operationMessages[operation] || 'Updating...';
      setChannelUpdateMessages(messages);

      const updatedMessages = {};

      const runRestrictions = async ({ channelId = null, channelIds = null } = {}) => {
        const apply = (selectedInventory.restriction_action || 'apply') === 'apply';
        const r = selectedInventory.restrictions || DEFAULT_RESTRICTION_FIELDS;
        return setRestrictionsRange(
          start_date,
          end_date,
          selectedRoomTypes,
          channelId,
          {
            blockType:
              r.closeOnArrival && r.closeOnDeparture
                ? 'master'
                : r.closeOnArrival
                  ? 'cta'
                  : r.closeOnDeparture
                    ? 'ctd'
                    : 'master',
            apply,
            reason: selectedInventory.reason || '',
            channelIds,
            restrictions: r,
          }
        );
      };

      try {
        if (operation === 'restrictions') {
          try {
            const result = allChannelsSelected
              ? await runRestrictions({ channelId: 'all' })
              : await runRestrictions({ channelIds: specificOtaIds });
            updatedMessages.all = result.success
              ? `${result.message || 'Restrictions updated'} (${result.room_types_updated || selectedRoomTypes.length} room type(s))`
              : (result.message || result.error || 'Error');
          } catch (err) {
            updatedMessages.all = err?.response?.data?.error || 'Error updating restrictions';
          }
        } else if (operation === 'sold_out') {
          // Apply → sold_out + stopSell:true; Clear → available + stopSell:false
          const stopOp = stopSellApply ? 'sold_out' : 'available';
          try {
            const result = allChannelsSelected
              ? await setAvailabilityRange(
                  start_date,
                  end_date,
                  selectedRoomTypes,
                  'all',
                  stopOp
                )
              : await setAvailabilityRange(
                  start_date,
                  end_date,
                  selectedRoomTypes,
                  null,
                  stopOp,
                  { channelIds: specificOtaIds }
                );
            updatedMessages.all = result.success
              ? `${stopSellApply ? 'Stop sell applied' : 'Stop sell cleared'} (${result.room_types_updated || selectedRoomTypes.length} room type(s), ${result.channels_updated || specificOtaIds.length || 0} channels, ${result.updated_count} day-record(s))`
              : (result.message || 'Error');
          } catch (err) {
            updatedMessages.all = err?.response?.data?.error || 'Error updating';
          }
        } else if (hasOtaTargets) {
          try {
            const result = allChannelsSelected
              ? await setAvailabilityRange(
                  start_date,
                  end_date,
                  selectedRoomTypes,
                  'all',
                  operation
                )
              : await setAvailabilityRange(
                  start_date,
                  end_date,
                  selectedRoomTypes,
                  null,
                  operation,
                  { channelIds: specificOtaIds }
                );
            updatedMessages.all = result.success
              ? (result.message
                || `Available inventory synced to all selected channels (${result.room_types_updated || selectedRoomTypes.length} room type(s), ${result.channels_updated || specificOtaIds.length || 0} channels, ${result.updated_count} day-record(s))`)
              : (result.message || result.error || 'Error');
          } catch (err) {
            updatedMessages.all = 'Error updating';
          }
        }

        if (pmsSelected) {
          try {
            const result = await setAvailabilityRange(
              start_date,
              end_date,
              selectedRoomTypes,
              'pms',
              operation
            );
            updatedMessages.pms = result.success
              ? `${result.operation} (${result.room_types_updated || selectedRoomTypes.length} room type(s), ${result.updated_count} day(s))`
              : (result.message || 'Error');
          } catch (err) {
            updatedMessages.pms = 'Error updating';
          }
        }

        setChannelUpdateMessages(updatedMessages);

        const failed = Object.values(updatedMessages).some(
          (msg) => typeof msg === 'string' && /error/i.test(msg)
        );
        if (!failed && Object.keys(updatedMessages).length > 0) {
          const copy =
            operation === 'sold_out'
              ? (stopSellApply
                ? RATE_SUCCESS_COPY.inventoryStopSellApply
                : RATE_SUCCESS_COPY.inventoryStopSellClear)
              : operation === 'restrictions'
                ? ((selectedInventory.restriction_action || 'apply') === 'apply'
                  ? RATE_SUCCESS_COPY.inventoryRestrictionsApply
                  : RATE_SUCCESS_COPY.inventoryRestrictionsClear)
                : RATE_SUCCESS_COPY.inventoryAvailable;
          setSuccessDialog(copy);
        }

        const cacheKey = `${dateOffset}-${dateOffset + visibleDatesCount}-${selectedChannel}`;
        delete cachedInventoryRef.current[cacheKey];
        Object.keys(cachedInventoryRef.current).forEach((key) => {
          if (key.startsWith(`${dateOffset}-${dateOffset + visibleDatesCount}-`)) {
            delete cachedInventoryRef.current[key];
          }
        });
        const refreshGrid = selectedChannel === 'all'
          ? fetchAllChannelInventory(dateOffset, channels.length > 0 ? channels : null, true)
          : fetchInventory(dateOffset, false, selectedChannel);
        refreshGrid.catch(() => {});
      } catch (error) {
        // Update error messages
        const errorMessages = {};
        Object.keys(messages).forEach(channelId => {
          errorMessages[channelId] = 'Error updating';
        });
        setChannelUpdateMessages(errorMessages);
        setErrorMessage('Error saving inventory. Some values may have been capped to channel limits.');
      }
    } catch (error) {setErrorMessage(error.response?.data?.error || error.message || 'Error saving inventory. Please try again.');
    } finally {
      setIsSaving(false);
      setLoading(false);
    }
  };

  // Filter listings by room type (memoized for performance)
  const filteredListings = React.useMemo(() => {
    return selectedRoomTypeFilter && selectedRoomTypeFilter !== 'all'
      ? listings.filter((listing) => listing.id.toString() === selectedRoomTypeFilter)
      : listings;
  }, [listings, selectedRoomTypeFilter]);

  // Room type filter changes don't require refetching - we just filter the existing data
  // The filteredListings memo already handles filtering, so no need for this useEffect
  
  // Restrictions / Stop Sell push to OTAs only, so PMS is not offered on those tabs
  const pmsChannelAllowed =
    selectedInventory.available !== 'restrictions' && selectedInventory.available !== 'sold_out';

  // Prepare channels data for display when "All Channels" is selected (memoized for performance)
  const pmsChannelMeta = React.useMemo(
    () => ({ id: 'pms', name: 'PMS', image_path: getStaticFileUrl('ota/pms.png') }),
    []
  );
  const displayChannels = React.useMemo(() => {
    if (selectedChannel !== 'all') return [];
    if (!hasChannels) return [pmsChannelMeta];
    // Channel View OFF (and not Full View): combined All Channels row; ON: every OTA as its own row
    if (!channelView && !fullView) {
      return [
        pmsChannelMeta,
        { id: 'combined', name: 'All Channels', isCombined: true },
      ];
    }
    return [pmsChannelMeta, ...(Array.isArray(channels) ? channels : [])];
  }, [selectedChannel, hasChannels, channelView, fullView, channels, pmsChannelMeta]);
  
  // Get inventory for a specific channel when "All Channels" is selected (memoized for performance)
  const getChannelInventory = React.useCallback((channelId, roomTypeId) => {
    if (selectedChannel !== 'all' || !allChannelsInventory[channelId]) {
      return null;
    }
    const channelInventory = allChannelsInventory[channelId];
    const roomTypeData = channelInventory.find(r => r.id.toString() === roomTypeId);
    return roomTypeData?.inventory || {};
  }, [selectedChannel, allChannelsInventory]);

  const formatNumber = React.useCallback((value) => {
    if (value === null || value === undefined) return '—';
    return parseInt(value).toString();
  }, []);

  /** Short label for dense grid: "Expedia Affiliate Network" → "Expedia" */
  const shortChannelName = React.useCallback((name) => {
    const raw = String(name || '').trim();
    if (!raw) return '';
    return raw.split(/\s+/)[0];
  }, []);

  const toggleChannelExpand = useCallback((channelKey) => {
    setExpandedChannels((prev) => ({
      ...prev,
      [channelKey]: !prev[channelKey],
    }));
  }, []);

  // Reset / default expand state when leaving All Channels / All Room Types view
  useEffect(() => {
    if (selectedChannel !== 'all' || selectedRoomTypeFilter !== 'all' || fullView) {
      setExpandedChannels({});
      return;
    }
    // Combined mode: keep PMS + All Channels expanded by default
    if (!channelView) {
      setExpandedChannels({ pms: false, combined: true });
    } else {
      setExpandedChannels({});
    }
  }, [selectedChannel, selectedRoomTypeFilter, fullView, channelView]);

  const handleChannelViewToggle = useCallback((checked) => {
    setChannelView(checked);
    if (checked) setFullView(false);
  }, []);

  const handleFullViewToggle = useCallback((checked) => {
    setFullView(checked);
    if (checked) setChannelView(false);
  }, []);

  return (
    <Box sx={getDashboardPageShellSx(currentTheme)}>
      {/* Header Section */}
      <Box sx={{ mb: isMobileLayout ? 1 : 3 }}>
        <DashboardHeader
          title="Manage Inventory"
          showInventorySectionTabs
          navLinks={[
            { url: '/finance/rates/', label: 'Manage Rate' },
          ]}
        />
      </Box>

      {/* Calendar Container */}
      <Paper
        elevation={0}
        sx={{
          ...getDashboardPaperSx(currentTheme, 'calendar'),
          minHeight: isMobileLayout ? 0 : '500px',
        }}
      >
              {/* Calendar Header */}
              <Box
                sx={{
                  mb: isMobileLayout ? 0 : 3,
                  ...(isMobileLayout ? inventoryMobileToolbarSx() : {}),
                  display: 'flex',
                  flexDirection: isMobileLayout ? 'column' : 'row',
                  alignItems: isMobileLayout ? 'stretch' : 'center',
                  justifyContent: 'space-between',
                  gap: isMobileLayout ? 1 : 2,
                }}
              >
                <Box sx={{
                  display: 'flex',
                  alignItems: 'center',
                  flexWrap: isMobileLayout ? 'wrap' : 'nowrap',
                  gap: isMobileLayout ? 0.75 : 2,
                  width: '100%',
                }}>
                <Typography variant={isMobileLayout ? 'subtitle1' : 'h6'} sx={{ fontWeight: 700, flex: isMobileLayout ? '1 1 auto' : undefined }}>
                  {dates[0]?.format('MMMM YYYY') || moment().format('MMMM YYYY')}
                </Typography>
                <IconButton 
                  size="small" 
                  onClick={() => {
                    setIsTransitioning(true);
                    const newStartDate = calendarStartDate.clone().subtract(visibleDatesCount, 'days');
                    setCalendarStartDate(newStartDate);
                  }}
                  disabled={loading}
                >
                  <ArrowBackIosIcon fontSize="small" />
                </IconButton>
                <IconButton 
                  size="small" 
                  onClick={() => {
                    setIsTransitioning(true);
                    const newStartDate = calendarStartDate.clone().add(visibleDatesCount, 'days');
                    setCalendarStartDate(newStartDate);
                  }}
                  disabled={loading}
                >
                  <ArrowForwardIosIcon fontSize="small" />
                </IconButton>
                <LocalizationProvider dateAdapter={AdapterMoment}>
                  <DatePicker
                    label="Start Date"
                    value={calendarStartDate}
                    onChange={(newValue) => {
                      if (newValue) {
                        setIsTransitioning(true);
                        setCalendarStartDate(newValue);
                      }
                    }}
                    slotProps={{
                      textField: {
                        size: 'small',
                        sx: { width: isMobileLayout ? '100%' : 150, flex: isMobileLayout ? '1 1 140px' : undefined },
                      },
                    }}
                    disabled={loading}
                  />
                </LocalizationProvider>
                {/* Full View / Combine toggles — desktop only; mobile lives in action row below */}
                {!isMobileLayout && hasChannels && (
                <>
                <FormControlLabel
                  control={
                    <Switch
                      checked={channelView && selectedChannel === 'all' && !fullView}
                      onChange={(e) => handleChannelViewToggle(e.target.checked)}
                      disabled={selectedChannel !== 'all' || fullView}
                      sx={{
                        '& .MuiSwitch-switchBase.Mui-checked': {
                          color: THEME_COLOR,
                        },
                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                          backgroundColor: THEME_COLOR,
                        },
                      }}
                    />
                  }
                  label={
                    <Typography variant="body2" sx={{ fontSize: '0.875rem', fontWeight: 500 }}>
                      Channel View
                    </Typography>
                  }
                  sx={{ ml: 2, width: 'auto' }}
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={fullView}
                      onChange={(e) => handleFullViewToggle(e.target.checked)}
                      disabled={selectedChannel !== 'all' || selectedRoomTypeFilter !== 'all'}
                      sx={{
                        '& .MuiSwitch-switchBase.Mui-checked': {
                          color: THEME_COLOR,
                        },
                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                          backgroundColor: THEME_COLOR,
                        },
                      }}
                    />
                  }
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Typography variant="body2" sx={{ fontSize: '0.875rem', fontWeight: 500 }}>
                        Full View
                      </Typography>
                      {selectedChannel !== 'all' || selectedRoomTypeFilter !== 'all' ? (
                        <Tooltip title="Full View requires 'All Channels' and 'All Room Types' to be selected">
                          <Typography variant="caption" sx={{ color: isDark ? '#999' : '#666', fontSize: '0.7rem' }}>
                            (All Channels & All Rooms)
                          </Typography>
                        </Tooltip>
                      ) : null}
                    </Box>
                  }
                  sx={{ ml: 1, width: 'auto' }}
                />
                </>
                )}
                </Box>
                
                {/* Room Type Filter and Action Buttons on Right */}
                <Box sx={{
                  display: 'flex',
                  flexDirection: isMobileLayout ? 'column' : 'row',
                  gap: 1,
                  alignItems: 'stretch',
                  width: isMobileLayout ? '100%' : 'auto',
                }}>
                  {isMobileLayout && hasChannels && (
                    <>
                      {/* Toggles row: two switches share the row without crowding the Action button */}
                      <Box
                        sx={{
                          display: 'grid',
                          gridTemplateColumns: '1fr 1fr',
                          gap: 1,
                          width: '100%',
                          alignItems: 'center',
                        }}
                      >
                        <FormControlLabel
                          control={
                            <Switch
                              size="small"
                              checked={channelView && selectedChannel === 'all' && !fullView}
                              onChange={(e) => handleChannelViewToggle(e.target.checked)}
                              disabled={selectedChannel !== 'all' || fullView}
                              sx={{
                                '& .MuiSwitch-switchBase.Mui-checked': {
                                  color: THEME_COLOR,
                                },
                                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                  backgroundColor: THEME_COLOR,
                                },
                              }}
                            />
                          }
                          label={
                            <Typography variant="body2" sx={{ fontSize: '0.78rem', fontWeight: 500, whiteSpace: 'nowrap' }}>
                              Channel View
                            </Typography>
                          }
                          sx={{ m: 0, minWidth: 0, overflow: 'hidden' }}
                        />
                        <FormControlLabel
                          control={
                            <Switch
                              size="small"
                              checked={fullView}
                              onChange={(e) => handleFullViewToggle(e.target.checked)}
                              disabled={selectedChannel !== 'all' || selectedRoomTypeFilter !== 'all'}
                              sx={{
                                '& .MuiSwitch-switchBase.Mui-checked': {
                                  color: THEME_COLOR,
                                },
                                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                  backgroundColor: THEME_COLOR,
                                },
                              }}
                            />
                          }
                          label={
                            <Typography variant="body2" sx={{ fontSize: '0.78rem', fontWeight: 500, whiteSpace: 'nowrap' }}>
                              Full View
                            </Typography>
                          }
                          sx={{ m: 0, minWidth: 0, overflow: 'hidden' }}
                        />
                      </Box>
                      <Button
                        variant="contained"
                        size="small"
                        startIcon={<Add />}
                        endIcon={<ArrowDropDownIcon />}
                        onClick={(e) => setAddMenuAnchor(e.currentTarget)}
                        disabled={loading || isTransitioning}
                        sx={{
                          backgroundColor: THEME_COLOR,
                          color: '#000',
                          fontWeight: 600,
                          width: '100%',
                          height: '40px',
                          '&:hover': {
                            backgroundColor: THEME_COLOR,
                            opacity: 0.9,
                          },
                        }}
                      >
                        Action
                      </Button>
                    </>
                  )}
                  {!isMobileLayout && (
                  <>
                  {/* Action Buttons */}
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<Add />}
                    endIcon={<ArrowDropDownIcon />}
                    onClick={(e) => setAddMenuAnchor(e.currentTarget)}
                    disabled={loading || isTransitioning}
                    sx={{
                      backgroundColor: THEME_COLOR,
                      color: '#000',
                      fontWeight: 600,
                      minWidth: isMobileLayout ? '100%' : 140,
                      height: '40px',
                      '&:hover': {
                        backgroundColor: THEME_COLOR,
                        opacity: 0.9,
                      },
                    }}
                  >
                    Action
                  </Button>
                  </>
                  )}
                  <Menu
                    anchorEl={addMenuAnchor}
                    open={Boolean(addMenuAnchor)}
                    onClose={() => setAddMenuAnchor(null)}
                    PaperProps={{
                      sx: {
                        ...inventoryMenuPaperSx(isDark, { minWidth: 220, mt: 1 }),
                      },
                    }}
                  >
                    <MenuItem
                    onClick={() => {
                        setAddMenuAnchor(null);
                        handleOpenDrawerWithOption('available');
                      }}
                      sx={{
                        color: themeColors.text,
                        '&:hover': {
                          backgroundColor: isDark ? 'rgba(76, 225, 182, 0.1)' : 'rgba(76, 225, 182, 0.05)',
                        },
                      }}
                    >
                      <ListItemIcon>
                        <CheckCircle sx={{ color: THEME_COLOR }} />
                      </ListItemIcon>
                      <ListItemText>Available</ListItemText>
                    </MenuItem>
                    <MenuItem
                      onClick={() => {
                        setAddMenuAnchor(null);
                        handleOpenDrawerWithOption('restrictions');
                      }}
                      sx={{
                        color: themeColors.text,
                        '&:hover': {
                          backgroundColor: isDark ? 'rgba(76, 225, 182, 0.1)' : 'rgba(76, 225, 182, 0.05)',
                        },
                      }}
                    >
                      <ListItemIcon>
                        <Block sx={{ color: THEME_COLOR }} />
                      </ListItemIcon>
                      <ListItemText>Restrictions</ListItemText>
                    </MenuItem>
                    <MenuItem
                      onClick={() => {
                        setAddMenuAnchor(null);
                        handleOpenDrawerWithOption('sold_out');
                      }}
                      sx={{
                        color: themeColors.text,
                        '&:hover': {
                          backgroundColor: isDark ? 'rgba(76, 225, 182, 0.1)' : 'rgba(76, 225, 182, 0.05)',
                        },
                      }}
                    >
                      <ListItemIcon>
                        <Cancel sx={{ color: THEME_COLOR }} />
                      </ListItemIcon>
                      <ListItemText>Stop Sell</ListItemText>
                    </MenuItem>
                    <MenuItem
                      onClick={() => {
                        setAddMenuAnchor(null);
                        handleOpenInventoryAdjust('add');
                      }}
                      sx={{
                        color: themeColors.text,
                        '&:hover': {
                          backgroundColor: isDark ? 'rgba(76, 225, 182, 0.1)' : 'rgba(76, 225, 182, 0.05)',
                        },
                      }}
                    >
                      <ListItemIcon>
                        <AddCircle sx={{ color: THEME_COLOR }} />
                      </ListItemIcon>
                      <ListItemText>Event Block</ListItemText>
                    </MenuItem>
                  </Menu>
                  {isMobileLayout ? (
                    <Box sx={inventoryMobileToolbarFilterRowSx()}>
                      <FormControl size="small" sx={{ minWidth: 0, width: '100%' }}>
                        <InputLabel>Room Type</InputLabel>
                        <Select
                          value={selectedRoomTypeFilter || 'all'}
                          label="Room Type"
                          onChange={(e) => setSelectedRoomTypeFilter(e.target.value)}
                          disabled={loading || isTransitioning}
                        >
                          <MenuItem value="all">All Room Types</MenuItem>
                          {(allRoomTypes.length > 0 ? allRoomTypes : listings).length > 0 ? (
                            (allRoomTypes.length > 0 ? allRoomTypes : listings).map((listing) => (
                              <MenuItem key={listing.id} value={listing.id.toString()}>
                                {listing.name || `Room Type ${listing.id}`}
                              </MenuItem>
                            ))
                          ) : (
                            <MenuItem disabled>Loading room types...</MenuItem>
                          )}
                        </Select>
                      </FormControl>
                      <ChannelSelect
                        value={selectedChannel}
                        onChange={(e) => setSelectedChannel(e.target.value)}
                        channels={channels}
                        label="Channel"
                        allLabel="OTA"
                        includeAll={hasChannels}
                        disabled={loading || isTransitioning}
                        size="small"
                        sx={{ minWidth: 0, width: '100%' }}
                      />
                    </Box>
                  ) : (
                  <FormControl size="small" sx={{ minWidth: 200, width: 'auto' }}>
                    <InputLabel>Room Type</InputLabel>
                    <Select
                      value={selectedRoomTypeFilter || 'all'}
                      label="Room Type"
                      onChange={(e) => setSelectedRoomTypeFilter(e.target.value)}
                      disabled={loading || isTransitioning}
                    >
                      <MenuItem value="all">All Room Types</MenuItem>
                      {(allRoomTypes.length > 0 ? allRoomTypes : listings).length > 0 ? (
                        (allRoomTypes.length > 0 ? allRoomTypes : listings).map((listing) => (
                          <MenuItem key={listing.id} value={listing.id.toString()}>
                            {listing.name || `Room Type ${listing.id}`}
                          </MenuItem>
                        ))
                      ) : (
                        <MenuItem disabled>Loading room types...</MenuItem>
                      )}
                    </Select>
                  </FormControl>
                  )}
                </Box>
              </Box>

              {/* Calendar Grid: date + channel always visible; skeleton only in inventory data rows */}
              <Box 
                sx={{ 
                  overflowX: 'auto',
                  overflowY: 'hidden',
                  width: '100%',
                  position: 'relative',
                  opacity: isTransitioning ? 0.7 : 1,
                  transition: 'opacity 0.2s ease-in-out',
                  minHeight: isMobileLayout ? 0 : '500px',
                  pointerEvents: isTransitioning ? 'none' : 'auto',
                }}
              >
                <Box sx={{
                  opacity: isTransitioning ? 0.75 : 1,
                  transition: 'opacity 0.2s ease-in-out',
                  ...inventoryGridScrollInnerSx(isMobileLayout),
                }}>

              {/* Date Headers — scrolls with inventory rows (single horizontal overflow on parent) */}
              <Box
                sx={{
                  ...inventoryGridRowSx(isMobileLayout, dates.length, 200, {
                    mb: 1,
                    zIndex: 10,
                    py: isMobileLayout ? 0 : 1,
                  }),
                }}
              >
                {isMobileLayout ? (
                  <MobileInventoryGridCorner isDark={isDark} label="Day" />
                ) : (
                <Box sx={inventoryChannelCornerCellSx(isDark)}>
                  <ChannelSelect
                    value={selectedChannel}
                    onChange={(e) => setSelectedChannel(e.target.value)}
                    channels={channels}
                    label="Channel"
                    includeAll={hasChannels}
                    disabled={loading || isTransitioning}
                    size="small"
                  />
                </Box>
                )}
                {dates.map((date) => {
                  const isToday = date.isSame(moment(), 'day');
                  const isWeekend = isWeekendDate(date);
                  return (
                  <Box
                    key={date.format('YYYY-MM-DD')}
                    sx={inventoryDateHeaderCellSx(isMobileLayout, isDark, isToday, isWeekend)}
                  >
                    <Typography variant="caption" sx={inventoryDateHeaderLabelSx(isMobileLayout)} display="block">
                      {date.format(isMobileLayout ? 'dd' : 'ddd')}
                    </Typography>
                    <Typography variant="body2" sx={inventoryDateHeaderDaySx(isMobileLayout, isToday)}>
                      {date.format('D')}
                    </Typography>
                  </Box>
                );
                })}
              </Box>

              {/* Only inventory data rows: skeleton mirrors Channel View / listings layout */}
              {isInventoryGridLoading && (selectedChannel === 'all' ? Object.keys(allChannelsInventory).length === 0 : listings.length === 0) ? (
                <InventoryGridSkeleton
                  isMobile={isMobileLayout}
                  dates={dates}
                  isDark={isDark}
                  theme={theme}
                  desktopLabelWidth={200}
                  mode={
                    selectedChannel === 'all'
                      ? (channelView || fullView ? 'channels' : 'combined')
                      : 'listings'
                  }
                  rowCount={
                    selectedChannel === 'all'
                      ? ((channelView || fullView)
                          ? Math.max(4, (channels?.length || 0) + 1)
                          : 2)
                      : Math.max(3, listings.length || allRoomTypes.length || 4)
                  }
                  withExpandedRooms={
                    selectedChannel === 'all'
                    && !channelView
                    && !fullView
                    && selectedRoomTypeFilter === 'all'
                  }
                  roomTypeCount={Math.min(4, allRoomTypes.length || listings.length || 3)}
                />
              ) : filteredListings.length === 0 && selectedChannel !== 'all' ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '300px', flexDirection: 'column', gap: 2 }}>
                  <Typography variant="h6">No inventory data available</Typography>
                </Box>
              ) : (
              <>
              {selectedChannel === 'all' ? (
                // Full View: Display all channels with all room types
                fullView && selectedRoomTypeFilter === 'all' ? (
                displayChannels.map((channel) => {
                    const channelIdKey = channel.id === 'pms' ? 'pms' : String(channel.id);
                    const channelInventory = allChannelsInventory[channelIdKey] || [];
                    
                    // Get all room types for this channel
                    const roomTypesForChannel = channelInventory.length > 0 
                      ? channelInventory 
                      : (allRoomTypes.length > 0 ? allRoomTypes : []);
                    
                    return (
                      <React.Fragment key={channel.id}>
                        {/* Channel Header Row */}
                        <Box
                          sx={{
                            ...inventoryGridRowSx(isMobileLayout, dates.length, 200, { mb: 0.5 }),
                          }}
                        >
                          <Box
                            sx={{
                              p: { xs: 0.5, sm: 1 },
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              borderRight: `1px solid ${themeColors.surfaceBorder}`,
                              backgroundColor: isDark ? 'rgba(76, 225, 182, 0.15)' : 'rgba(76, 225, 182, 0.1)',
                              color: THEME_COLOR,
                              fontWeight: 600,
                              position: 'sticky',
                              left: 0,
                              zIndex: 2,
                              borderBottom: `2px solid ${THEME_COLOR}`,
                            }}
                          >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: 0 }}>
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  width: 32,
                                  height: 32,
                                  flexShrink: 0,
                                }}
                              >
                                <ChannelImage channel={channel} size={32} />
                              </Box>
                              <Typography variant="body2" sx={{ fontWeight: 700, color: THEME_COLOR, fontSize: { xs: '0.75rem', sm: '0.875rem' } }} title={channel.name}>
                                {shortChannelName(channel.name)}
                              </Typography>
                            </Box>
                            {(() => {
                              // Calculate total across all room types for this channel
                              let totalAllocated = 0;
                              roomTypesForChannel.forEach((roomType) => {
                                const roomTypeData = channelInventory.find(r => r.id.toString() === roomType.id.toString());
                                if (roomTypeData) {
                                  totalAllocated += roomTypeData.allocated_rooms || 0;
                                }
                              });
                              return (
                                <Chip
                                  label={formatNumber(totalAllocated)}
                                  size="small"
                                  sx={{
                                    height: 20,
                                    fontSize: '0.65rem',
                                    fontWeight: 700,
                                    backgroundColor: THEME_COLOR,
                                    color: '#000',
                                    '& .MuiChip-label': {
                                      px: 0.75,
                                    },
                                  }}
                                />
                              );
                            })()}
                          </Box>
                          {dates.map((date) => {
                            const dateStr = date.format('YYYY-MM-DD');
                            // Calculate total across all room types for this channel and date
                            let totalAvailable = 0;
                            let totalRooms = 0;
                            let totalBooked = 0;
                            let hasAnyInventory = false;
                            
                            roomTypesForChannel.forEach((roomType) => {
                              const roomTypeData = channelInventory.find(r => r.id.toString() === roomType.id.toString());
                              const inventory = roomTypeData?.inventory?.[dateStr];
                              if (inventory && roomTypeData) {
                                hasAnyInventory = true;
                                // Both PMS and channels use the same structure:
                                // - allocated_rooms at listing level is the total capacity
                                // - available_rooms in inventory is what's available
                                // - booked = total - available
                                const availableRooms = inventory.available_rooms !== undefined && inventory.available_rooms !== null
                                  ? inventory.available_rooms
                                  : 0;
                                const total = roomTypeData.allocated_rooms || 0;
                                const booked = Math.max(0, total - availableRooms);
                                
                                totalAvailable += availableRooms;
                                totalRooms += total;
                                totalBooked += booked;
                              }
                            });
                            
                            const isLoading = isInitialLoad || !hasLoadedOnce;
                            const isEmpty = !hasAnyInventory && hasLoadedOnce;
                            const bookingPercentage = totalRooms > 0 ? Math.round((totalBooked / totalRooms) * 100) : 0;
                            
                            return (
                              <Paper
                                key={dateStr}
                                elevation={0}
                                sx={{
                                  p: { xs: 0.5, sm: 1 },
                                  textAlign: 'center',
                                  minHeight: { xs: '35px', sm: '40px' },
                                  height: { xs: '35px', sm: '40px' },
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  gap: 0.5,
                                  borderBottom: `2px solid ${THEME_COLOR}`,
                                  borderRight: `1px solid ${themeColors.surfaceBorder}`,
                                  backgroundColor: isLoading
                                    ? (theme.palette.mode === 'dark' ? alpha(theme.palette.common.white, 0.06) : alpha(theme.palette.common.black, 0.04))
                                    : (isDark ? 'rgba(76, 225, 182, 0.15)' : 'rgba(76, 225, 182, 0.1)'),
                                }}
                              >
                                {!isLoading && (
                                  <>
                                    <Typography
                                      sx={{
                                        fontWeight: 700,
                                        color: THEME_COLOR,
                                        fontSize: { xs: '0.8rem', sm: '0.9rem' },
                                        lineHeight: 1.2,
                                      }}
                                    >
                                      {isEmpty ? '—' : formatNumber(totalAvailable)}
                                    </Typography>
                                    {!isEmpty && totalRooms > 0 && (
                                      <Typography
                                        sx={{
                                          fontWeight: 500,
                                          color: THEME_COLOR,
                                          fontSize: { xs: '0.65rem', sm: '0.7rem' },
                                          opacity: 0.8,
                                          lineHeight: 1.2,
                                        }}
                                      >
                                        ({bookingPercentage}%)
                                      </Typography>
                                    )}
                                  </>
                                )}
                              </Paper>
                            );
                          })}
                        </Box>
                        
                        {/* Room Type Rows for this Channel */}
                        {roomTypesForChannel.map((roomType, roomTypeIndex) => {
                          const roomTypeData = channelInventory.find(r => r.id.toString() === roomType.id.toString());
                          const inventories = roomTypeData?.inventory || {};
                          
                          return (
                            <Box
                              key={`${channel.id}-${roomType.id}`}
                              sx={{
                                ...inventoryGridRowSx(isMobileLayout, dates.length, 200, {
                                  mb: roomTypeIndex === roomTypesForChannel.length - 1 ? 1 : 0.5,
                                }),
                              }}
                            >
                              <Box
                                sx={{
                                  p: { xs: 0.35, sm: 1 },
                                  pl: { xs: 0.5, sm: 2 },
                                  display: 'flex',
                                  alignItems: 'center',
                                  borderRight: `1px solid ${themeColors.surfaceBorder}`,
                                  backgroundColor: isDark ? themeColors.surface : '#ffffff',
                                  color: themeColors.text,
                                  position: 'sticky',
                                  left: 0,
                                  zIndex: 1,
                                  borderLeft: isMobileLayout ? 'none' : `3px solid ${THEME_COLOR}`,
                                  minWidth: 0,
                                  overflow: 'hidden',
                                }}
                              >
                                <InventoryRoomTypeLabel
                                  roomType={roomType}
                                  isMobile={isMobileLayout}
                                  isDark={isDark}
                                  fontWeight={500}
                                  endAdornment={(
                                    <Chip
                                      label={formatNumber(roomTypeData?.allocated_rooms || 0)}
                                      size="small"
                                      sx={{
                                        height: isMobileLayout ? 14 : 18,
                                        fontSize: isMobileLayout ? '0.5rem' : '0.6rem',
                                        fontWeight: 700,
                                        backgroundColor: THEME_COLOR,
                                        color: '#000',
                                        flexShrink: 0,
                                        '& .MuiChip-label': {
                                          px: isMobileLayout ? 0.35 : 0.5,
                                        },
                                      }}
                                    />
                                  )}
                                />
                              </Box>
                              
                              {dates.map((date) => {
                                const dateStr = date.format('YYYY-MM-DD');
                                const inventory = inventories[dateStr];
                                const isEmpty = !inventory && hasLoadedOnce;
                                const isBlocked = !!(inventory && inventory.available === false);
                                
                                let availableRooms = 0;
                                if (inventory) {
                                  if (channel.id === 'pms') {
                                    availableRooms = inventory.available_rooms !== undefined && inventory.available_rooms !== null
                                      ? inventory.available_rooms
                                      : (inventory.total_rooms !== undefined ? (inventory.total_rooms || 0) - (inventory.booked_rooms || 0) : 0);
                                  } else {
                                    availableRooms = inventory.available_rooms !== undefined && inventory.available_rooms !== null
                                      ? inventory.available_rooms
                                      : (inventory.allocated_rooms !== undefined ? (inventory.allocated_rooms || 0) - (inventory.booked_rooms || 0) : 0);
                                  }
                                }
                                
                                const isSoldOut = !isBlocked && (!!(inventory && inventory.sold_out === true) || availableRooms === 0);
                                const hasInventory = inventory && inventory.available !== false && !inventory.sold_out && availableRooms > 0 && hasLoadedOnce;
                                const isLoading = isInitialLoad || !hasLoadedOnce;
                                
                                let inventoryDisplayText = null;
                                if (inventory) {
                                  if (isBlocked) {
                                    inventoryDisplayText = formatNumber(availableRooms);
                                  } else if (isSoldOut || availableRooms === 0) {
                                    inventoryDisplayText = 'Sold Out';
                                  } else {
                                    inventoryDisplayText = formatNumber(availableRooms);
                                  }
                                }
                                
                                return (
                                  <StatusCellTooltip
                                    key={dateStr}
                                    title={getInventoryCellTooltip({ inventory, availableRooms })}
                                  >
                                  <Paper
                                    elevation={0}
                                    sx={{
                                      p: { xs: 0.25, sm: 0.5 },
                                      textAlign: 'center',
                                      cursor: 'pointer',
                                      minHeight: { xs: '30px', sm: '35px' },
                                      height: { xs: '30px', sm: '35px' },
                                      display: 'flex',
                                      flexDirection: 'column',
                                      justifyContent: 'center',
                                      alignItems: 'center',
                                      position: 'relative',
                                      border: isLoading
                                        ? `1px solid ${theme.palette.divider}`
                                        : isEmpty || isBlocked || isSoldOut
                                        ? `1px solid #c1807b`
                                        : `1px solid #a0c49e`,
                                      borderRadius: isEmpty || isBlocked || isSoldOut ? 2 : 1,
                                      ...inventoryRateCellFillSx(isDark, false, isWeekendDate(date)),
                                      '&:hover': {
                                        ...inventoryRateCellFillSx(isDark, false, isWeekendDate(date))['&:hover'],
                                        transform: 'scale(1.02)',
                                        zIndex: 10,
                                        boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.2)}`,
                                      },
                                      transition: 'all 0.2s ease-in-out',
                                    }}
                                    onClick={() => handleDateClick(roomType.id.toString(), dateStr, channel.id === 'pms' ? 'pms' : channel.id.toString())}
                                  >
                                    {!isLoading && (
                                      <>
                                        <Typography
                                          sx={{
                                            color: (isSoldOut || (inventoryDisplayText === 'Sold Out'))
                                              ? theme.palette.grey[600]
                                              : isEmpty
                                              ? theme.palette.error.main
                                              : theme.palette.text.secondary,
                                            fontWeight: 600,
                                            fontSize: { xs: '0.7rem', sm: '0.8rem' },
                                            lineHeight: 1.1,
                                            width: '100%',
                                            textAlign: 'center',
                                            whiteSpace: 'nowrap',
                                          }}
                                        >
                                          {inventoryDisplayText !== null ? inventoryDisplayText : '—'}
                                        </Typography>
                                        {isBlocked && (
                                          <Box sx={inventoryStatusDotSx(isMobileLayout, theme.palette.error.main)} />
                                        )}
                                        {!isBlocked && isSoldOut && (
                                          <Box sx={inventoryStatusDotSx(isMobileLayout, theme.palette.grey[500])} />
                                        )}
                                        {!isBlocked && !isSoldOut && hasInventory && (
                                          <Box sx={inventoryStatusDotSx(isMobileLayout, theme.palette.success.main)} />
                                        )}
                                      </>
                                    )}
                                  </Paper>
                                  </StatusCellTooltip>
                                );
                              })}
                            </Box>
                          );
                        })}
                      </React.Fragment>
                    );
                  })
                ) : (
                  // Normal View: Display channels as rows when "All Channels" is selected
                  <>
                    {displayChannels.map((channel) => {
                  const isCombined = Boolean(channel.isCombined);
                  const firstOtaId =
                    Array.isArray(channels) && channels[0] != null
                      ? String(channels[0].id)
                      : null;
                  const channelIdKey = channel.id === 'pms'
                    ? 'pms'
                    : isCombined
                      ? 'combined'
                      : String(channel.id);
                  const channelInventory = isCombined
                    ? (firstOtaId ? (allChannelsInventory[firstOtaId] || []) : [])
                    : (allChannelsInventory[channelIdKey] || []);
                  
                  // When "all" is selected, sum up totals across all room types
                  // When specific room type is selected, use that room type's data
                  let inventories = {};
                  if (selectedRoomTypeFilter === 'all') {
                    // Sum up totals across all room types for each date
                    const dateTotals = {};
                    channelInventory.forEach((roomType) => {
                      Object.entries(roomType.inventory || {}).forEach(([dateStr, inv]) => {
                        if (!dateTotals[dateStr]) {
                          dateTotals[dateStr] = {
                            total_rooms: 0,
                            allocated_rooms: 0,
                            booked_rooms: 0,
                            available_rooms: 0, // Sum of available rooms (total - booked)
                            available: true,
                          };
                        }
                        if (channel.id === 'pms') {
                          // For PMS: sum available_rooms (total_rooms - booked_rooms) which represents rooms available for booking
                          const pmsAvailable =
                            inv.available_rooms !== undefined && inv.available_rooms !== null
                              ? inv.available_rooms
                              : Math.max(0, (inv.total_rooms || 0) - (inv.booked_rooms || 0));
                          dateTotals[dateStr].available_rooms += pmsAvailable;
                          dateTotals[dateStr].total_rooms += inv.total_rooms || 0;
                          dateTotals[dateStr].booked_rooms += inv.booked_rooms || 0;
                          dateTotals[dateStr].available = dateTotals[dateStr].available && inv.available !== false;
                        } else {
                          // For channels: sum available_rooms (allocated - booked) and allocated_rooms
                          const channelAvailable =
                            inv.available_rooms !== undefined && inv.available_rooms !== null
                              ? inv.available_rooms
                              : Math.max(0, (inv.allocated_rooms || 0) - (inv.booked_rooms || 0));
                          dateTotals[dateStr].available_rooms += channelAvailable;
                          dateTotals[dateStr].allocated_rooms += inv.allocated_rooms || 0;
                          dateTotals[dateStr].booked_rooms += inv.booked_rooms || 0;
                          dateTotals[dateStr].available = dateTotals[dateStr].available && inv.available !== false;
                        }
                      });
                    });
                    inventories = dateTotals;
                  } else {
                    const roomTypeData = channelInventory.find(r => r.id.toString() === selectedRoomTypeFilter);
                    inventories = roomTypeData?.inventory || {};
                  }

                  const isExpanded = Boolean(expandedChannels[channelIdKey]);
                  const canExpand = selectedRoomTypeFilter === 'all';
                  const roomTypesForChannel = channelInventory.length > 0
                    ? channelInventory
                    : (allRoomTypes.length > 0 ? allRoomTypes : []);
                  // Combined row edits all OTAs via channelId "all" (single Aiosell push)
                  const channelIdForClick = channel.id === 'pms'
                    ? 'pms'
                    : isCombined
                      ? 'all'
                      : channel.id.toString();
                  
                  return (
                    <React.Fragment key={channel.id}>
                    <Box
                      sx={{
                        ...inventoryGridRowSx(isMobileLayout, dates.length, 200, {
                          mb: isExpanded && canExpand ? 0.5 : 1,
                        }),
                      }}
                    >
                      <Box
                        sx={{
                          ...inventoryStickyLabelCellSx(isMobileLayout, isDark),
                          cursor: canExpand ? 'pointer' : 'default',
                          userSelect: 'none',
                          transition: 'background-color 0.2s ease, box-shadow 0.2s ease',
                          borderBottom: isExpanded && canExpand
                            ? `2px solid ${THEME_COLOR}`
                            : undefined,
                          backgroundColor: isExpanded && canExpand
                            ? (isDark ? 'rgba(76, 225, 182, 0.12)' : 'rgba(76, 225, 182, 0.08)')
                            : undefined,
                          '&:hover': canExpand
                            ? {
                                backgroundColor: isDark
                                  ? 'rgba(76, 225, 182, 0.16)'
                                  : 'rgba(76, 225, 182, 0.12)',
                              }
                            : undefined,
                        }}
                        onClick={canExpand ? () => toggleChannelExpand(channelIdKey) : undefined}
                        role={canExpand ? 'button' : undefined}
                        aria-expanded={canExpand ? isExpanded : undefined}
                        title={canExpand ? (isExpanded ? 'Hide room types' : 'Show room types') : undefined}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: 0, flexDirection: isMobileLayout ? 'column' : 'row' }}>
                          {canExpand && !isMobileLayout && (
                            <ExpandMoreIcon
                              sx={{
                                fontSize: 18,
                                color: THEME_COLOR,
                                flexShrink: 0,
                                transition: 'transform 0.25s ease',
                                transform: isExpanded ? 'rotate(0deg)' : 'rotate(-90deg)',
                              }}
                            />
                          )}
                          {isCombined ? (
                            <Typography
                              variant="body2"
                              noWrap
                              title="All Channels"
                              sx={{
                                fontWeight: isExpanded ? 700 : 600,
                                color: themeColors.text,
                                fontSize: { xs: '0.7rem', sm: '0.875rem' },
                                minWidth: 0,
                                textAlign: isMobileLayout ? 'center' : 'left',
                              }}
                            >
                              {isMobileLayout ? 'OTA' : 'All Channels'}
                            </Typography>
                          ) : (
                            <>
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              width: isMobileLayout ? 22 : 32,
                              height: isMobileLayout ? 22 : 32,
                              flexShrink: 0,
                            }}
                          >
                            <ChannelImage channel={channel} size={isMobileLayout ? 22 : 32} />
                          </Box>
                          {!isMobileLayout && (
                          <Typography variant="body2" sx={{ fontWeight: isExpanded ? 700 : 500, color: themeColors.text, fontSize: { xs: '0.75rem', sm: '0.875rem' } }} title={channel.name}>
                            {shortChannelName(channel.name)}
                          </Typography>
                          )}
                            </>
                          )}
                        </Box>
                          {(() => {
                            if (selectedRoomTypeFilter !== 'all') {
                              // Show allocated_rooms for specific room type
                              const roomTypeData = channelInventory.find(r => r.id.toString() === selectedRoomTypeFilter);
                              const count = roomTypeData?.allocated_rooms || 0;
                            return (
                              <Chip
                                label={formatNumber(count)}
                                size="small"
                                sx={{
                                  height: isMobileLayout ? 14 : 20,
                                  fontSize: isMobileLayout ? '0.5rem' : '0.65rem',
                                  fontWeight: 700,
                                  backgroundColor: THEME_COLOR,
                                  color: '#000',
                                  '& .MuiChip-label': {
                                    px: isMobileLayout ? 0.35 : 0.75,
                                  },
                                }}
                              />
                            );
                            } else {
                              // Show total across all room types for this channel
                              const totalCount = channelInventory.reduce((sum, roomType) => {
                                return sum + (roomType.allocated_rooms || 0);
                              }, 0);
                            return (
                              <Chip
                                label={formatNumber(totalCount)}
                                size="small"
                                sx={{
                                  height: isMobileLayout ? 14 : 20,
                                  fontSize: isMobileLayout ? '0.5rem' : '0.65rem',
                                  fontWeight: 700,
                                  backgroundColor: THEME_COLOR,
                                  color: '#000',
                                  '& .MuiChip-label': {
                                    px: isMobileLayout ? 0.35 : 0.75,
                                  },
                                }}
                              />
                            );
                            }
                          })()}
                      </Box>
                      
                      {dates.map((date) => {
                        const dateStr = date.format('YYYY-MM-DD');
                        const inventory = inventories[dateStr];
                      // Check if this specific box (date + channel) is selected
                      const currentChannelId = channelIdForClick;
                      const isSelected = selectedDates.some((sd) => 
                        sd.start === dateStr && 
                        sd.channelId === currentChannelId &&
                        (sd.roomTypeId === selectedRoomTypeFilter || selectedRoomTypeFilter === 'all')
                      );
                      const isEmpty = !inventory && hasLoadedOnce;
                      // UI Functionality Priority:
                      // 1. Blocked (available === false) → RED (highest priority, even if inventory = 0)
                      // 2. Sold Out (sold_out === true or available_rooms === 0) → GRAY
                      // 3. Available (available === true && sold_out === false) → GREEN
                      const isBlocked = !!(inventory && inventory.available === false);
                      
                        // Calculate availableRooms first
                        let availableRooms = 0;
                        if (inventory) {
                          if (channel.id === 'pms') {
                            // For PMS
                            availableRooms = inventory.available_rooms !== undefined && inventory.available_rooms !== null
                              ? inventory.available_rooms
                              : (inventory.total_rooms !== undefined ? (inventory.total_rooms || 0) - (inventory.booked_rooms || 0) : 0);
                          } else {
                            // For channels
                            availableRooms = inventory.available_rooms !== undefined && inventory.available_rooms !== null
                              ? inventory.available_rooms
                              : (inventory.allocated_rooms !== undefined ? (inventory.allocated_rooms || 0) - (inventory.booked_rooms || 0) : 0);
                          }
                        }
                        
                        // Sold out: sold_out === true or available_rooms === 0 (check after calculating availableRooms)
                        const isSoldOut = !isBlocked && (!!(inventory && inventory.sold_out === true) || availableRooms === 0);
                        const hasInventory = inventory && inventory.available !== false && !inventory.sold_out && availableRooms > 0 && hasLoadedOnce;
                        const isLoading = isInitialLoad || !hasLoadedOnce;
                        
                        // Get inventory values to display:
                        // - Available: Show available / total (e.g., "5 / 10")
                        // - Sold Out: Show "Sold Out" when inventory is 0
                        // - Blocked: Show total only (red)
                        let inventoryValue = null;
                        let inventoryDisplayText = null;
                        if (inventory) {
                          if (isBlocked) {
                            // Blocked: Show available rooms (with red border)
                            inventoryValue = availableRooms;
                            inventoryDisplayText = formatNumber(availableRooms);
                          } else if (isSoldOut || availableRooms === 0) {
                            // Sold Out: Show "Sold Out" when inventory is 0 or marked as sold out
                            inventoryValue = 0;
                            inventoryDisplayText = 'Sold Out';
                          } else {
                            // Available: Show available rooms
                            inventoryValue = availableRooms;
                            inventoryDisplayText = formatNumber(availableRooms);
                          }
                        }
                        
                        return (
                          <StatusCellTooltip
                            key={dateStr}
                            title={getInventoryCellTooltip({ inventory, availableRooms })}
                          >
                          <Paper
                            elevation={0}
                            sx={inventoryDataCellSx(isMobileLayout, {
                              p: { xs: 0.5, sm: 1 },
                              textAlign: 'center',
                              cursor: 'pointer',
                              minHeight: { xs: '50px', sm: '60px' },
                              height: { xs: '50px', sm: '60px' },
                              display: 'flex',
                              flexDirection: 'column',
                              justifyContent: 'center',
                              alignItems: 'center',
                              position: 'relative',
                              border: isSelected
                                ? `2px solid ${theme.palette.primary.main}`
                                : isLoading
                                ? `1px solid ${theme.palette.divider}`
                                : isEmpty || isBlocked
                                ? `1px solid #c1807b`
                                : `1px solid #a0c49e`,
                              borderRadius: isEmpty || isBlocked ? 2 : 1,
                              ...inventoryRateCellFillSx(isDark, isSelected, isWeekendDate(date)),
                            })}
                            onClick={() => handleDateClick(selectedRoomTypeFilter, dateStr, channelIdForClick)}
                          >
                            {hasInventory && !isLoading && (
                              <Box sx={inventoryStatusDotSx(isMobileLayout, theme.palette.success.main)} />
                            )}
                            
                            {!isLoading && (isEmpty || isBlocked || isSoldOut) && (
                              <Box
                                sx={inventoryStatusDotSx(
                                  isMobileLayout,
                                  isSoldOut ? theme.palette.grey[500] : theme.palette.error.main,
                                )}
                              />
                            )}
                            
                            {!isLoading && (
                              inventoryDisplayText !== null ? (
                                <Typography
                                  sx={{
                                    fontWeight: 600,
                                    fontSize: { xs: '0.85rem', sm: '1.125rem' },
                                    lineHeight: 1.1,
                                    width: '100%',
                                    textAlign: 'center',
                                    whiteSpace: 'nowrap',
                                  }}
                                >
                                  {inventoryDisplayText}
                                </Typography>
                              ) : (
                                <Typography
                                  sx={{
                                    fontSize: { xs: '0.85rem', sm: '1.125rem' },
                                    color: 'text.secondary',
                                    width: '100%',
                                    textAlign: 'center',
                                  }}
                                >
                                  —
                                </Typography>
                              )
                            )}
                          </Paper>
                          </StatusCellTooltip>
                        );
                      })}
                    </Box>

                    {canExpand && (
                      <Collapse in={isExpanded} timeout={280} unmountOnExit>
                        <Box
                          sx={{
                            mb: 1.25,
                            ml: { xs: 0, sm: 0.5 },
                            pl: { xs: 0, sm: 0.5 },
                            borderLeft: { xs: 'none', sm: `2px solid ${alpha(THEME_COLOR, 0.4)}` },
                            '@keyframes invRoomReveal': {
                              from: { opacity: 0, transform: 'translateY(-6px)' },
                              to: { opacity: 1, transform: 'translateY(0)' },
                            },
                          }}
                        >
                          {roomTypesForChannel.map((roomType, roomTypeIndex) => {
                            const roomTypeData = channelInventory.find(
                              (r) => r.id.toString() === roomType.id.toString()
                            );
                            const roomInventories = roomTypeData?.inventory || {};

                            return (
                              <Box
                                key={`${channel.id}-${roomType.id}`}
                                className="inv-room-row"
                                sx={{
                                  ...inventoryGridRowSx(isMobileLayout, dates.length, 200, {
                                    mb: roomTypeIndex === roomTypesForChannel.length - 1 ? 0 : 0.5,
                                  }),
                                  animation: 'invRoomReveal 0.28s ease both',
                                  animationDelay: `${Math.min(roomTypeIndex, 8) * 35}ms`,
                                }}
                              >
                                <Box
                                  sx={{
                                    p: { xs: 0.35, sm: 1 },
                                    pl: { xs: 0.5, sm: 2 },
                                    display: 'flex',
                                    alignItems: 'center',
                                    borderRight: `1px solid ${themeColors.surfaceBorder}`,
                                    backgroundColor: isDark ? themeColors.surface : '#ffffff',
                                    color: themeColors.text,
                                    position: 'sticky',
                                    left: 0,
                                    zIndex: 1,
                                    borderLeft: isMobileLayout ? 'none' : `3px solid ${THEME_COLOR}`,
                                    minWidth: 0,
                                    overflow: 'hidden',
                                  }}
                                >
                                  <InventoryRoomTypeLabel
                                    roomType={roomType}
                                    isMobile={isMobileLayout}
                                    isDark={isDark}
                                    fontWeight={600}
                                    endAdornment={(
                                      <Chip
                                        label={formatNumber(roomTypeData?.allocated_rooms || 0)}
                                        size="small"
                                        sx={{
                                          height: isMobileLayout ? 14 : 18,
                                          fontSize: isMobileLayout ? '0.5rem' : '0.6rem',
                                          fontWeight: 700,
                                          backgroundColor: THEME_COLOR,
                                          color: '#000',
                                          flexShrink: 0,
                                          '& .MuiChip-label': { px: isMobileLayout ? 0.35 : 0.5 },
                                        }}
                                      />
                                    )}
                                  />
                                </Box>

                                {dates.map((date) => {
                                  const dateStr = date.format('YYYY-MM-DD');
                                  const inventory = roomInventories[dateStr];
                                  const isEmpty = !inventory && hasLoadedOnce;
                                  const isBlocked = !!(inventory && inventory.available === false);

                                  let availableRooms = 0;
                                  if (inventory) {
                                    if (channel.id === 'pms') {
                                      availableRooms =
                                        inventory.available_rooms !== undefined && inventory.available_rooms !== null
                                          ? inventory.available_rooms
                                          : (inventory.total_rooms !== undefined
                                            ? (inventory.total_rooms || 0) - (inventory.booked_rooms || 0)
                                            : 0);
                                    } else {
                                      availableRooms =
                                        inventory.available_rooms !== undefined && inventory.available_rooms !== null
                                          ? inventory.available_rooms
                                          : (inventory.allocated_rooms !== undefined
                                            ? (inventory.allocated_rooms || 0) - (inventory.booked_rooms || 0)
                                            : 0);
                                    }
                                  }

                                  const isSoldOut =
                                    !isBlocked &&
                                    (!!(inventory && inventory.sold_out === true) || availableRooms === 0);
                                  const hasInventory =
                                    inventory &&
                                    inventory.available !== false &&
                                    !inventory.sold_out &&
                                    availableRooms > 0 &&
                                    hasLoadedOnce;
                                  const isLoadingCell = isInitialLoad || !hasLoadedOnce;

                                  let inventoryDisplayText = null;
                                  if (inventory) {
                                    if (isBlocked) {
                                      inventoryDisplayText = formatNumber(availableRooms);
                                    } else if (isSoldOut || availableRooms === 0) {
                                      inventoryDisplayText = 'Sold Out';
                                    } else {
                                      inventoryDisplayText = formatNumber(availableRooms);
                                    }
                                  }

                                  return (
                                    <StatusCellTooltip
                                      key={dateStr}
                                      title={getInventoryCellTooltip({ inventory, availableRooms })}
                                    >
                                    <Paper
                                      elevation={0}
                                      sx={{
                                        p: { xs: 0.25, sm: 0.5 },
                                        textAlign: 'center',
                                        cursor: 'pointer',
                                        minHeight: { xs: '34px', sm: '40px' },
                                        height: { xs: '34px', sm: '40px' },
                                        display: 'flex',
                                        flexDirection: 'column',
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                        position: 'relative',
                                        border: isLoadingCell
                                          ? `1px solid ${theme.palette.divider}`
                                          : isEmpty || isBlocked || isSoldOut
                                          ? `1px solid #c1807b`
                                          : `1px solid #a0c49e`,
                                        borderRadius: isEmpty || isBlocked || isSoldOut ? 2 : 1.5,
                                        ...inventoryRateCellFillSx(isDark, false, isWeekendDate(date)),
                                        transition: 'transform 0.18s ease, box-shadow 0.18s ease',
                                        '&:hover': {
                                          ...inventoryRateCellFillSx(isDark, false, isWeekendDate(date))['&:hover'],
                                          transform: 'scale(1.03)',
                                          zIndex: 10,
                                          boxShadow: `0 2px 10px ${alpha(THEME_COLOR, 0.22)}`,
                                        },
                                      }}
                                      onClick={() =>
                                        handleDateClick(
                                          roomType.id.toString(),
                                          dateStr,
                                          channelIdForClick
                                        )
                                      }
                                    >
                                      {!isLoadingCell && (
                                        <>
                                          <Typography
                                            sx={{
                                              color:
                                                isSoldOut || inventoryDisplayText === 'Sold Out'
                                                  ? '#c1807b'
                                                  : isEmpty
                                                  ? theme.palette.error.main
                                                  : theme.palette.text.primary,
                                              fontWeight: 650,
                                              fontSize:
                                                inventoryDisplayText === 'Sold Out'
                                                  ? { xs: '0.58rem', sm: '0.68rem' }
                                                  : { xs: '0.75rem', sm: '0.875rem' },
                                              lineHeight: 1.15,
                                            }}
                                          >
                                            {inventoryDisplayText !== null ? inventoryDisplayText : '—'}
                                          </Typography>
                                          {(isBlocked || isSoldOut || isEmpty || hasInventory) ? (
                                            <Box
                                              sx={inventoryStatusDotSx(
                                                isMobileLayout,
                                                isBlocked
                                                  ? theme.palette.error.main
                                                  : isSoldOut || isEmpty
                                                  ? theme.palette.grey[500]
                                                  : theme.palette.success.main,
                                              )}
                                            />
                                          ) : null}
                                        </>
                                      )}
                                    </Paper>
                                    </StatusCellTooltip>
                                  );
                                })}
                              </Box>
                            );
                          })}
                        </Box>
                      </Collapse>
                    )}
                    </React.Fragment>
                  );
                    })}
                  </>
                )
              ) : (
                // Display room types as rows when specific channel is selected
                <>
                  {/* Channel Header Row with Total */}
                  <Box
                    sx={{
                      ...inventoryGridRowSx(isMobileLayout, dates.length, 200, { mb: 0.5 }),
                    }}
                  >
                    <Box
                      sx={{
                        p: { xs: 0.5, sm: 1 },
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        borderRight: `1px solid ${themeColors.surfaceBorder}`,
                        backgroundColor: isDark ? 'rgba(76, 225, 182, 0.15)' : 'rgba(76, 225, 182, 0.1)',
                        color: THEME_COLOR,
                        fontWeight: 600,
                        position: 'sticky',
                        left: 0,
                        zIndex: 2,
                        borderBottom: `2px solid ${THEME_COLOR}`,
                      }}
                    >
                      <Typography variant="body2" sx={{ fontWeight: 700, color: THEME_COLOR, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                        {selectedChannel === 'pms'
                          ? 'PMS'
                          : shortChannelName(
                              channels.find((c) => c.id.toString() === selectedChannel)?.name || 'Channel'
                            )}
                      </Typography>
                      {(() => {
                        // Calculate total across all room types for this channel
                        let totalAllocated = 0;
                        filteredListings.forEach((listing) => {
                          totalAllocated += listing.allocated_rooms || 0;
                        });
                        return (
                          <Chip
                            label={formatNumber(totalAllocated)}
                            size="small"
                            sx={{
                              height: 20,
                              fontSize: '0.65rem',
                              fontWeight: 700,
                              backgroundColor: THEME_COLOR,
                              color: '#000',
                              '& .MuiChip-label': {
                                px: 0.75,
                              },
                            }}
                          />
                        );
                      })()}
                    </Box>
                    {dates.map((date) => {
                      const dateStr = date.format('YYYY-MM-DD');
                      // Calculate total across all room types for this date
                      let totalAvailable = 0;
                      let totalRooms = 0;
                      let totalBooked = 0;
                      let hasAnyInventory = false;
                      
                      filteredListings.forEach((listing) => {
                        const inventory = listing.inventory?.[dateStr];
                        if (inventory) {
                          hasAnyInventory = true;
                          // Both PMS and channels use the same structure:
                          // - allocated_rooms at listing level is the total capacity
                          // - available_rooms in inventory is what's available
                          // - booked = total - available
                          const availableRooms = inventory.available_rooms !== undefined && inventory.available_rooms !== null
                            ? inventory.available_rooms
                            : 0;
                          const total = listing.allocated_rooms || 0;
                          const booked = Math.max(0, total - availableRooms);
                          
                          totalAvailable += availableRooms;
                          totalRooms += total;
                          totalBooked += booked;
                        }
                      });
                      
                      const isLoading = isInitialLoad || !hasLoadedOnce;
                      const isEmpty = !hasAnyInventory && hasLoadedOnce;
                      const bookingPercentage = totalRooms > 0 ? Math.round((totalBooked / totalRooms) * 100) : 0;
                      
                      return (
                        <Paper
                          key={dateStr}
                          elevation={0}
                          sx={{
                            p: { xs: 0.5, sm: 1 },
                            textAlign: 'center',
                            minHeight: { xs: '35px', sm: '40px' },
                            height: { xs: '35px', sm: '40px' },
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 0.5,
                            borderBottom: `2px solid ${THEME_COLOR}`,
                            borderRight: `1px solid ${themeColors.surfaceBorder}`,
                            backgroundColor: isLoading
                              ? (theme.palette.mode === 'dark' ? alpha(theme.palette.common.white, 0.06) : alpha(theme.palette.common.black, 0.04))
                              : (isDark ? 'rgba(76, 225, 182, 0.15)' : 'rgba(76, 225, 182, 0.1)'),
                          }}
                        >
                          {!isLoading && (
                            <>
                              <Typography
                                sx={{
                                  fontWeight: 700,
                                  color: THEME_COLOR,
                                  fontSize: { xs: '0.8rem', sm: '0.9rem' },
                                  lineHeight: 1.2,
                                }}
                              >
                                {isEmpty ? '—' : formatNumber(totalAvailable)}
                              </Typography>
                              {!isEmpty && totalRooms > 0 && (
                                <Typography
                                  sx={{
                                    fontWeight: 500,
                                    color: THEME_COLOR,
                                    fontSize: { xs: '0.65rem', sm: '0.7rem' },
                                    opacity: 0.8,
                                    lineHeight: 1.2,
                                  }}
                                >
                                  ({bookingPercentage}%)
                                </Typography>
                              )}
                            </>
                          )}
                        </Paper>
                      );
                    })}
                  </Box>
                  
                  {/* Room Type Rows */}
                  {filteredListings.map((listing, listingIndex) => (
                  <Box
                    key={listing.id}
                    sx={{
                      ...inventoryGridRowSx(isMobileLayout, dates.length, 200, {
                        mb: listingIndex === filteredListings.length - 1 ? 1 : 0.5,
                      }),
                    }}
                  >
                    <Box
                      sx={{
                        p: { xs: 0.5, sm: 1 },
                          pl: { xs: 1.5, sm: 2 },
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        borderRight: `1px solid ${theme.palette.divider}`,
                        backgroundColor: theme.palette.background.paper,
                        color: theme.palette.text.primary,
                        position: 'sticky',
                        left: 0,
                        zIndex: 1,
                          borderLeft: `3px solid ${THEME_COLOR}`,
                      }}
                    >
                        <Typography variant="body2" sx={{ fontWeight: 500, fontSize: { xs: '0.7rem', sm: '0.8rem' } }}>
                        {listing.name}
                      </Typography>
                      {(() => {
                        const count = listing.allocated_rooms || 0;
                        return (
                          <Chip
                            label={formatNumber(count)}
                            size="small"
                            sx={{
                              height: 18,
                              fontSize: '0.6rem',
                              fontWeight: 700,
                              backgroundColor: THEME_COLOR,
                              color: '#000',
                              '& .MuiChip-label': {
                                px: 0.5,
                              },
                            }}
                          />
                        );
                      })()}
                    </Box>
                    
                    {dates.map((date) => {
                      const dateStr = date.format('YYYY-MM-DD');
                      const inventory = listing.inventory?.[dateStr];
                      const currentChannelId = selectedChannel === 'pms' ? 'pms' : selectedChannel.toString();
                      const isSelected = selectedDates.some((sd) => 
                        sd.start === dateStr && 
                        sd.roomTypeId === listing.id.toString() &&
                        sd.channelId === currentChannelId
                      );
                      const isEmpty = !inventory && hasLoadedOnce;
                      const isBlocked = !!(inventory && inventory.available === false);
                        
                        let availableRooms = 0;
                      if (inventory) {
                        if (selectedChannel === 'pms') {
                            availableRooms = inventory.available_rooms !== undefined && inventory.available_rooms !== null
                            ? inventory.available_rooms
                            : (inventory.total_rooms !== undefined ? (inventory.total_rooms || 0) - (inventory.booked_rooms || 0) : 0);
                          } else {
                            availableRooms = inventory.available_rooms !== undefined && inventory.available_rooms !== null
                            ? inventory.available_rooms
                            : (inventory.allocated_rooms !== undefined ? (inventory.allocated_rooms || 0) - (inventory.booked_rooms || 0) : 0);
                          }
                        }
                        
                        const isSoldOut = !isBlocked && (!!(inventory && inventory.sold_out === true) || availableRooms === 0);
                        const hasInventory = inventory && inventory.available !== false && !inventory.sold_out && availableRooms > 0 && hasLoadedOnce;
                        const isLoading = isInitialLoad || !hasLoadedOnce;
                        
                        let inventoryDisplayText = null;
                        if (inventory) {
                          if (isBlocked) {
                            inventoryDisplayText = formatNumber(availableRooms);
                          } else if (isSoldOut || availableRooms === 0) {
                            inventoryDisplayText = 'Sold Out';
                          } else {
                            inventoryDisplayText = formatNumber(availableRooms);
                        }
                      }
                      
                      return (
                        <StatusCellTooltip
                          key={dateStr}
                          title={getInventoryCellTooltip({ inventory, availableRooms })}
                        >
                        <Paper
                          elevation={0}
                          sx={{
                              p: { xs: 0.25, sm: 0.5 },
                            textAlign: 'center',
                            cursor: 'pointer',
                              minHeight: { xs: '30px', sm: '35px' },
                              height: { xs: '30px', sm: '35px' },
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center',
                            alignItems: 'center',
                              position: 'relative',
                            border: isSelected
                              ? `2px solid ${theme.palette.primary.main}`
                              : isLoading
                              ? `1px solid ${theme.palette.divider}`
                              : isEmpty || isBlocked || isSoldOut
                              ? `1px solid #c1807b`
                              : `1px solid #a0c49e`,
                            borderRadius: isEmpty || isBlocked || isSoldOut ? 2 : 1,
                            ...inventoryRateCellFillSx(isDark, isSelected, isWeekendDate(date)),
                            '&:hover': {
                              ...inventoryRateCellFillSx(isDark, isSelected, isWeekendDate(date))['&:hover'],
                              transform: 'scale(1.02)',
                              zIndex: 10,
                              boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.2)}`,
                            },
                            transition: 'all 0.2s ease-in-out',
                          }}
                          onClick={() => handleDateClick(listing.id.toString(), dateStr)}
                        >
                          {!isLoading && (
                            <>
                              <Typography
                                sx={{
                                    color: (isSoldOut || (inventoryDisplayText === 'Sold Out'))
                                      ? theme.palette.grey[600]
                                    : isEmpty
                                    ? theme.palette.error.main
                                    : theme.palette.text.secondary,
                                    fontWeight: 600,
                                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                                  lineHeight: 1.1,
                                  width: '100%',
                                  textAlign: 'center',
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                  {inventoryDisplayText !== null ? inventoryDisplayText : '—'}
                              </Typography>
                                {isBlocked && (
                                  <Box sx={inventoryStatusDotSx(isMobileLayout, theme.palette.error.main)} />
                                )}
                                {!isBlocked && isSoldOut && (
                                  <Box sx={inventoryStatusDotSx(isMobileLayout, theme.palette.grey[500])} />
                                )}
                                {!isBlocked && !isSoldOut && hasInventory && (
                                  <Box sx={inventoryStatusDotSx(isMobileLayout, theme.palette.success.main)} />
                                )}
                            </>
                          )}
                        </Paper>
                        </StatusCellTooltip>
                      );
                    })}
                  </Box>
                  ))}
                </>
              )}
              </>
              )
              }
                </Box>
              </Box>
      </Paper>

      {/* Drawer for editing inventory (same width as check-in drawer) */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={closeUpdateInventoryDrawer}
        sx={getDrawerRootSx()}
        PaperProps={{
          sx: {
            width: { xs: '100%', sm: 600 },
            maxWidth: '100vw',
            display: 'flex',
            flexDirection: 'column',
            p: 0,
          },
        }}
      >
        <Box ref={drawerContainerRef} sx={{ display: 'flex', flexDirection: 'column', height: '100%', position: 'relative' }}>
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            p: 1.5,
            ...inventoryDrawerHeaderSx(isDark),
            position: 'relative',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <InventoryIcon sx={{ color: THEME_COLOR, fontSize: 24 }} />
            <Typography variant="h6" sx={{ color: themeColors.text, fontWeight: 600 }}>
              {inventoryAdjustMode ? 'Event Block' : 'Update Inventory'}
            </Typography>
          </Box>
          <IconButton 
            onClick={closeUpdateInventoryDrawer} 
            disabled={isSaving} 
            sx={{ color: isDark ? '#999' : '#666' }}
          >
            <CloseIcon />
          </IconButton>
          {/* Processing bar at header lower border */}
          {isSaving && (
            <LinearProgress 
              sx={{ 
                position: 'absolute',
                bottom: 0,
                left: 0,
                right: 0,
                height: 2,
                backgroundColor: 'transparent',
                '& .MuiLinearProgress-bar': {
                  backgroundColor: THEME_COLOR,
                },
              }} 
            />
          )}
        </Box>
        
        {/* Content */}
        <Box sx={{ 
          flex: 1, 
          overflowY: 'auto', 
          overflowX: 'hidden', 
          p: 1.5,
          pr: 2.5, // Add padding-right to move scrollbar away from content
          '&::-webkit-scrollbar': { 
            width: '8px',
          },
          '&::-webkit-scrollbar-track': { 
            background: 'transparent',
          },
          '&::-webkit-scrollbar-thumb': { 
            background: isDark ? '#555' : '#ccc',
            borderRadius: '4px',
            '&:hover': {
              background: isDark ? '#666' : '#aaa',
            },
          },
        }}>
          {/* Error message display */}
          {errorMessage && (
            <Alert 
              severity="error" 
              onClose={() => setErrorMessage(null)}
              sx={{ mb: 2 }}
            >
              {errorMessage}
            </Alert>
          )}
          
          {inventoryAdjustMode ? (
            // Inventory +/- Mode
            <EventBlockAdjustView
              mode={inventoryAdjustMode}
              onModeChange={setInventoryAdjustMode}
              inventoryBlocks={inventoryBlocks}
              loading={inventoryBlockLoading}
              isSaving={isSaving}
              onRefresh={fetchInventoryBlocks}
              onEdit={(block) => {
                setEditingInventoryBlock(block);
                setInventoryBlockFormData({
                  room_type_id: block.room_type_id || '',
                  block_type: block.block_type || 'MANUAL',
                  start_date: block.start_date ? moment(block.start_date) : null,
                  end_date: block.end_date ? moment(block.end_date) : null,
                  start_time: parseInventoryBlockTime(block.start_time) || defaultInventoryBlockStartTime(),
                  end_time: parseInventoryBlockTime(block.end_time) || defaultInventoryBlockEndTime(),
                  num_rooms: block.num_rooms || 1,
                  reason: block.reason || '',
                  is_active: block.is_active !== undefined ? block.is_active : true,
                  selectedChannels: deriveBlockChannelSelection(block),
                  selected_room_ids: Array.isArray(block.room_ids)
                    ? block.room_ids.map((id) => Number(id)).filter(Boolean)
                    : [],
                });
                setInventoryAdjustMode('add');
              }}
              onToggleActive={handleToggleInventoryBlockActive}
              formData={inventoryBlockFormData}
              onFormDataChange={setInventoryBlockFormData}
              errors={inventoryBlockErrors}
              onErrorsChange={setInventoryBlockErrors}
              allRoomTypes={allRoomTypes.length > 0 ? allRoomTypes : listings}
              isDark={isDark}
              channels={channels}
              getInventoryData={getInventoryData}
              editingBlock={editingInventoryBlock}
            />
          ) : (
            // Regular Inventory Update Mode
          <Grid container spacing={2}>
            {/* Available / Restrictions / Stop Sell */}
            <Grid item xs={12}>
              <ToggleButtonGroup
                value={selectedInventory.available}
                exclusive
                onChange={(e, newValue) => {
                  if (newValue !== null) {
                    setSelectedInventory({ ...selectedInventory, available: newValue });
                    // Restrictions / Stop Sell are OTA-only; Available = PMS + All Channels
                    if (newValue === 'restrictions' || newValue === 'sold_out') {
                      setSelectedChannels(hasChannels ? ['all'] : []);
                    } else {
                      setSelectedChannels(hasChannels ? [...DEFAULT_RATE_CHANNEL_SELECTION] : ['pms']);
                    }
                    setChannelUpdateMessages({});
                  }
                }}
                aria-label="inventory availability"
                fullWidth
                size="small"
                sx={{
                  display: 'flex',
                  width: '100%',
                  '& .MuiToggleButton-root': {
                    textTransform: 'none',
                    flex: 1,
                    py: 0.75,
                    px: { xs: 0.5, sm: 1, md: 1.5 },
                    minHeight: 36,
                    color: theme.palette.text.primary,
                    borderColor: theme.palette.divider,
                    fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.8rem' },
                    '&:hover': {
                      backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)',
                    },
                    '&.Mui-selected[value="available"]': {
                      backgroundColor: THEME_COLOR,
                      color: '#000',
                      fontWeight: 600,
                      '&:hover': {
                        backgroundColor: THEME_COLOR,
                        opacity: 0.9,
                      },
                    },
                    '&.Mui-selected[value="restrictions"]': {
                      backgroundColor: theme.palette.error.main,
                      color: theme.palette.error.contrastText,
                      '&:hover': {
                        backgroundColor: theme.palette.error.dark,
                      },
                    },
                    '&.Mui-selected[value="sold_out"]': {
                      backgroundColor: theme.palette.grey[500],
                      color: theme.palette.common.white,
                      '&:hover': {
                        backgroundColor: theme.palette.grey[600],
                      },
                    },
                  },
                }}
              >
                <ToggleButton value="available" aria-label="available">
                  Available
                </ToggleButton>
                <ToggleButton value="restrictions" aria-label="restrictions">
                  Restrictions
                </ToggleButton>
                <ToggleButton value="sold_out" aria-label="stop sell">
                  Stop Sell
                </ToggleButton>
              </ToggleButtonGroup>
            </Grid>

            {selectedInventory.available === 'available' && (
              <Grid item xs={12}>
                <Alert severity="info" sx={{ py: 0.75 }}>
                  Available inventory syncs to all selected channels. Each date is sent with remaining rooms after bookings and blocks, matching the calendar — not the full room-type count.
                </Alert>
              </Grid>
            )}

            {/* Room Type — multi-select chips (Select All + per type) */}
            <Grid item xs={12}>
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="subtitle2">Room Type</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {selectedRoomTypes.length} selected
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {(() => {
                    const drawerRoomTypes = allRoomTypes.length > 0 ? allRoomTypes : listings;
                    const allIds = drawerRoomTypes.map((rt) => rt.id.toString());
                    const allSelected =
                      allIds.length > 0 && allIds.every((id) => selectedRoomTypes.includes(id));
                    const chipSelectedSx = {
                      cursor: 'pointer',
                      fontWeight: 700,
                      backgroundColor: 'transparent',
                      color: 'inherit',
                      borderColor: THEME_COLOR,
                      borderWidth: 1.5,
                      '& .MuiChip-label, & .MuiChip-label *': {
                        color: 'inherit',
                      },
                      '& .MuiCheckbox-root': { color: THEME_COLOR, p: 0.25 },
                      '&:hover': {
                        backgroundColor: alpha(THEME_COLOR, 0.08),
                        borderColor: THEME_COLOR,
                      },
                    };
                    const chipOutlinedSx = {
                      cursor: 'pointer',
                      fontWeight: 500,
                      backgroundColor: 'transparent',
                      borderColor: alpha(THEME_COLOR, 0.45),
                      borderWidth: 1,
                      '& .MuiCheckbox-root': { p: 0.25, color: alpha(THEME_COLOR, 0.7) },
                      '&:hover': {
                        backgroundColor: alpha(THEME_COLOR, 0.08),
                        borderColor: THEME_COLOR,
                      },
                    };
                    return (
                      <>
                        <Chip
                          clickable
                          onClick={() => {
                            if (allSelected) {
                              setSelectedRoomTypes([]);
                              setSelectedRoomType(null);
                            } else {
                              setSelectedRoomTypes(allIds);
                              setSelectedRoomType(allIds[0] || null);
                            }
                          }}
                          variant="outlined"
                          label={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <Checkbox
                                size="small"
                                checked={allSelected}
                                indeterminate={
                                  selectedRoomTypes.length > 0 && !allSelected
                                }
                                tabIndex={-1}
                                disableRipple
                                sx={{ pointerEvents: 'none' }}
                              />
                              <Box
                                component="span"
                                sx={{
                                  fontSize: '0.875rem',
                                  fontWeight: allSelected ? 700 : 600,
                                  color: 'inherit',
                                }}
                              >
                                Select All
                              </Box>
                            </Box>
                          }
                          sx={allSelected ? chipSelectedSx : chipOutlinedSx}
                        />
                        {drawerRoomTypes.map((roomType) => {
                          const id = roomType.id.toString();
                          const selected = selectedRoomTypes.includes(id);
                          return (
                            <Chip
                              key={id}
                              clickable
                              onClick={() => {
                                setSelectedRoomTypes((prev) => {
                                  const next = selected
                                    ? prev.filter((x) => x !== id)
                                    : [...prev, id];
                                  setSelectedRoomType(next[0] || null);
                                  return next;
                                });
                              }}
                              variant="outlined"
                              label={
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  <Checkbox
                                    size="small"
                                    checked={selected}
                                    tabIndex={-1}
                                    disableRipple
                                    sx={{ pointerEvents: 'none' }}
                                  />
                                  <Box
                                    component="span"
                                    sx={{
                                      fontSize: '0.875rem',
                                      fontWeight: selected ? 700 : 500,
                                      color: 'inherit',
                                    }}
                                  >
                                    {roomType.name || `Room Type ${roomType.id}`}
                                  </Box>
                                </Box>
                              }
                              sx={selected ? chipSelectedSx : chipOutlinedSx}
                            />
                          );
                        })}
                      </>
                    );
                  })()}
                </Box>
              </Box>
            </Grid>

            {/* Date Range */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom>
                Date Range
              </Typography>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <LocalizationProvider dateAdapter={AdapterMoment}>
                  <DatePicker
                    label="From"
                    value={dateRange[0]}
                    minDate={moment().startOf('day')}
                    onChange={(newValue) => {
                      if (newValue) {
                        const newFrom = newValue.isBefore(moment().startOf('day'), 'day') ? moment().startOf('day') : newValue;
                        const maxTo = newFrom.clone().add(1, 'year');
                        let newTo = dateRange[1] && dateRange[1].isAfter(newFrom) ? dateRange[1] : newFrom.clone().add(1, 'day');
                        if (newTo.isAfter(maxTo)) newTo = maxTo;
                        setDateRange([newFrom, newTo]);
                        // Preserve room type and channel from original selection
                        const existingSelection = selectedDates[0] || {};
                        setSelectedDates([{ 
                          start: newFrom.format('YYYY-MM-DD'), 
                          end: newTo.format('YYYY-MM-DD'),
                          roomTypeId: existingSelection.roomTypeId || selectedRoomType,
                          channelId: existingSelection.channelId || (selectedChannel === 'pms' ? 'pms' : selectedChannel.toString())
                        }]);
                      }
                    }}
                    slotProps={{
                      ...drawerOverlays.datePickerSlotProps,
                      textField: { size: 'small', fullWidth: true }
                    }}
                  />
                  <DatePicker
                    label="To"
                    value={dateRange[1]}
                    onChange={(newValue) => {
                    if (newValue && newValue.isAfter(dateRange[0])) {
                      const maxTo = dateRange[0].clone().add(1, 'year');
                      const toValue = newValue.isAfter(maxTo) ? maxTo : newValue;
                      setDateRange([dateRange[0], toValue]);
                      const existingSelection = selectedDates[0] || {};
                      setSelectedDates([{ 
                        start: dateRange[0].format('YYYY-MM-DD'), 
                        end: toValue.format('YYYY-MM-DD'),
                        roomTypeId: existingSelection.roomTypeId || selectedRoomType,
                        channelId: existingSelection.channelId || (selectedChannel === 'pms' ? 'pms' : selectedChannel.toString())
                      }]);
                    }
                    }}
                    minDate={dateRange[0]?.clone().add(1, 'day')}
                    maxDate={dateRange[0]?.clone().add(1, 'year')}
                    slotProps={{
                      ...drawerOverlays.datePickerSlotProps,
                      textField: { size: 'small', fullWidth: true }
                    }}
                  />
                </LocalizationProvider>
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                Date range cannot exceed 1 year
              </Typography>
            </Grid>

            {/* Day Selection - Single Line */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom>
                Day Selection
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', pl: 2 }}>
                <FormControlLabel
                  value="custom"
                  control={
                    <Radio
                      checked={daySelectionType === 'custom'}
                  onChange={(e) => {
                        setDaySelectionType('custom');
                      setSelectedDays([0, 1, 2, 3, 4, 5, 6]);
                      }}
                    />
                  }
                  label="Custom"
                />
                      <FormControlLabel
                  value="weekdays"
                        control={
                    <Radio
                      checked={daySelectionType === 'weekdays'}
                            onChange={(e) => {
                        setDaySelectionType('weekdays');
                        setSelectedDays([1, 2, 3, 4, 5]);
                      }}
                    />
                  }
                  label="Weekdays"
                />
                <FormControlLabel
                  value="weekends"
                  control={
                    <Radio
                      checked={daySelectionType === 'weekends'}
                      onChange={(e) => {
                        setDaySelectionType('weekends');
                        setSelectedDays([0, 6]);
                      }}
                    />
                  }
                  label="Weekends"
                />
              </Box>
              {daySelectionType === 'custom' && (
                <Box sx={{ mt: 2, pl: 2 }}>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {[
                      { value: 0, label: 'Sun', shortLabel: 'S', color: '#FF6B6B' },
                      { value: 1, label: 'Mon', shortLabel: 'M', color: '#4ECDC4' },
                      { value: 2, label: 'Tue', shortLabel: 'T', color: '#45B7D1' },
                      { value: 3, label: 'Wed', shortLabel: 'W', color: '#FFA07A' },
                      { value: 4, label: 'Thu', shortLabel: 'T', color: '#98D8C8' },
                      { value: 5, label: 'Fri', shortLabel: 'F', color: '#F7DC6F' },
                      { value: 6, label: 'Sat', shortLabel: 'S', color: '#BB8FCE' },
                    ].map((day) => {
                      const isSelected = selectedDays.includes(day.value);
                      const isDark = theme.palette.mode === 'dark';
                      return (
                        <Chip
                          key={day.value}
                          label={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.7rem' }}>
                                {day.shortLabel}
                              </Typography>
                              <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>
                                {day.label}
                              </Typography>
                            </Box>
                          }
                          onClick={() => {
                            if (isSelected) {
                              setSelectedDays(selectedDays.filter(d => d !== day.value));
                            } else {
                              setSelectedDays([...selectedDays, day.value]);
                            }
                          }}
                          variant={isSelected ? 'filled' : 'outlined'}
                          sx={{
                            cursor: 'pointer',
                            fontWeight: isSelected ? 600 : 400,
                            backgroundColor: isSelected ? day.color : 'transparent',
                            color: isSelected ? '#fff' : themeColors.subtleText,
                            borderColor: isSelected ? day.color : themeColors.surfaceBorder,
                            borderWidth: isSelected ? 2 : 1,
                            borderStyle: 'solid',
                            height: 36,
                            transition: 'all 0.2s ease-in-out',
                            '&:hover': {
                              backgroundColor: isSelected ? day.color : alpha(day.color, 0.1),
                              borderColor: day.color,
                              transform: 'scale(1.05)',
                              boxShadow: isSelected 
                                ? `0 2px 8px ${alpha(day.color, 0.4)}`
                                : `0 2px 4px ${alpha(day.color, 0.2)}`,
                            },
                            '&:active': {
                              transform: 'scale(0.95)',
                            },
                          }}
                        />
                      );
                    })}
                  </Box>
                </Box>
              )}
            </Grid>

            {/* Restrictions — full inventory restriction fields (no Stop Sell) */}
            {selectedInventory.available === 'restrictions' && (
              <Grid item xs={12}>
                <Box
                  sx={{
                    borderRadius: 2,
                    border: `1px solid ${alpha(THEME_COLOR, 0.35)}`,
                    background: isDark
                      ? `linear-gradient(165deg, ${alpha(THEME_COLOR, 0.08)} 0%, ${alpha('#000', 0.2)} 100%)`
                      : `linear-gradient(165deg, ${alpha(THEME_COLOR, 0.1)} 0%, ${alpha(THEME_COLOR, 0.02)} 100%)`,
                    p: 1.5,
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 1,
                      mb: 1.5,
                      flexWrap: 'wrap',
                    }}
                  >
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                        Inventory restrictions
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Pushed to selected OTAs · Stop Sell is on its own tab
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 0.75 }}>
                      {[
                        { value: 'apply', label: 'Apply' },
                        { value: 'clear', label: 'Clear all' },
                      ].map((opt) => {
                        const on = (selectedInventory.restriction_action || 'apply') === opt.value;
                        return (
                          <Chip
                            key={opt.value}
                            label={opt.label}
                            onClick={() =>
                              setSelectedInventory({
                                ...selectedInventory,
                                restriction_action: opt.value,
                              })
                            }
                            variant="outlined"
                            sx={{
                              cursor: 'pointer',
                              fontWeight: on ? 700 : 500,
                              backgroundColor: on ? THEME_COLOR : 'transparent',
                              color: on ? '#000' : 'inherit',
                              borderColor: on ? THEME_COLOR : alpha(THEME_COLOR, 0.45),
                              borderWidth: on ? 1.5 : 1,
                              '&:hover': {
                                backgroundColor: on ? THEME_COLOR : alpha(THEME_COLOR, 0.08),
                                borderColor: THEME_COLOR,
                              },
                            }}
                          />
                        );
                      })}
                    </Box>
                  </Box>

                  {/* Access — CTA / CTD */}
                  <Typography
                    variant="caption"
                    sx={{
                      display: 'block',
                      fontWeight: 700,
                      letterSpacing: 0.4,
                      textTransform: 'uppercase',
                      color: 'text.secondary',
                      mb: 0.75,
                    }}
                  >
                    Access
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1.75 }}>
                    {[
                      {
                        key: 'closeOnArrival',
                        label: 'Close on Arrival',
                        hint: 'CTA',
                      },
                      {
                        key: 'closeOnDeparture',
                        label: 'Close on Departure',
                        hint: 'CTD',
                      },
                    ].map((item) => {
                      const r = selectedInventory.restrictions || DEFAULT_RESTRICTION_FIELDS;
                      const on = !!r[item.key];
                      const disabled = selectedInventory.restriction_action === 'clear';
                      return (
                        <Chip
                          key={item.key}
                          disabled={disabled}
                          label={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                              <Box
                                sx={{
                                  width: 14,
                                  height: 14,
                                  borderRadius: 0.5,
                                  border: `1.5px solid ${on ? '#000' : alpha(THEME_COLOR, 0.7)}`,
                                  backgroundColor: on ? '#000' : 'transparent',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  flexShrink: 0,
                                }}
                              >
                                {on && (
                                  <Box
                                    sx={{
                                      width: 6,
                                      height: 6,
                                      borderRadius: 0.25,
                                      backgroundColor: THEME_COLOR,
                                    }}
                                  />
                                )}
                              </Box>
                              <Box component="span" sx={{ fontWeight: on ? 700 : 500 }}>
                                {item.label}
                              </Box>
                              <Box
                                component="span"
                                sx={{
                                  fontSize: '0.65rem',
                                  opacity: 0.7,
                                  fontWeight: 600,
                                }}
                              >
                                {item.hint}
                              </Box>
                            </Box>
                          }
                          onClick={() => {
                            if (disabled) return;
                            setSelectedInventory({
                              ...selectedInventory,
                              restrictions: {
                                ...r,
                                [item.key]: !on,
                              },
                            });
                          }}
                          variant="outlined"
                          sx={{
                            cursor: disabled ? 'default' : 'pointer',
                            opacity: disabled ? 0.5 : 1,
                            backgroundColor: on ? THEME_COLOR : 'transparent',
                            color: on ? '#000' : 'inherit',
                            borderColor: on ? THEME_COLOR : alpha(THEME_COLOR, 0.45),
                            borderWidth: on ? 1.5 : 1,
                            height: 36,
                            '&:hover': disabled
                              ? undefined
                              : {
                                  backgroundColor: on ? THEME_COLOR : alpha(THEME_COLOR, 0.08),
                                  borderColor: THEME_COLOR,
                                },
                          }}
                        />
                      );
                    })}
                  </Box>

                  {/* Stay length */}
                  <Typography
                    variant="caption"
                    sx={{
                      display: 'block',
                      fontWeight: 700,
                      letterSpacing: 0.4,
                      textTransform: 'uppercase',
                      color: 'text.secondary',
                      mb: 0.75,
                    }}
                  >
                    Stay length
                  </Typography>
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: {
                        xs: '1fr 1fr',
                        sm: 'repeat(3, 1fr)',
                      },
                      gap: 1,
                      mb: 1.75,
                      opacity: selectedInventory.restriction_action === 'clear' ? 0.45 : 1,
                      pointerEvents:
                        selectedInventory.restriction_action === 'clear' ? 'none' : 'auto',
                    }}
                  >
                    {[
                      { key: 'minimumStay', label: 'Min stay' },
                      { key: 'maximumStay', label: 'Max stay' },
                      { key: 'exactStayArrival', label: 'Exact stay arrival' },
                      { key: 'minimumStayArrival', label: 'Min stay arrival' },
                      { key: 'maximumStayArrival', label: 'Max stay arrival' },
                    ].map((field) => {
                      const r = selectedInventory.restrictions || DEFAULT_RESTRICTION_FIELDS;
                      return (
                        <TextField
                          key={field.key}
                          label={field.label}
                          type="number"
                          size="small"
                          value={r[field.key] ?? ''}
                          onChange={(e) => {
                            const v = e.target.value;
                            if (v !== '' && Number(v) < 0) return;
                            setSelectedInventory({
                              ...selectedInventory,
                              restrictions: { ...r, [field.key]: v },
                            });
                          }}
                          inputProps={{ min: 0, step: 1 }}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              backgroundColor: isDark
                                ? alpha('#fff', 0.03)
                                : alpha('#fff', 0.7),
                              '& fieldset': {
                                borderColor: alpha(THEME_COLOR, 0.35),
                              },
                              '&:hover fieldset': {
                                borderColor: THEME_COLOR,
                              },
                              '&.Mui-focused fieldset': {
                                borderColor: THEME_COLOR,
                              },
                            },
                          }}
                        />
                      );
                    })}
                  </Box>

                  {/* Booking window */}
                  <Typography
                    variant="caption"
                    sx={{
                      display: 'block',
                      fontWeight: 700,
                      letterSpacing: 0.4,
                      textTransform: 'uppercase',
                      color: 'text.secondary',
                      mb: 0.75,
                    }}
                  >
                    Booking window
                  </Typography>
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: { xs: '1fr 1fr', sm: '1fr 1fr' },
                      gap: 1,
                      opacity: selectedInventory.restriction_action === 'clear' ? 0.45 : 1,
                      pointerEvents:
                        selectedInventory.restriction_action === 'clear' ? 'none' : 'auto',
                    }}
                  >
                    {[
                      {
                        key: 'minimumAdvanceReservation',
                        label: 'Min advance (days)',
                      },
                      {
                        key: 'maximumAdvanceReservation',
                        label: 'Max advance (days)',
                      },
                    ].map((field) => {
                      const r = selectedInventory.restrictions || DEFAULT_RESTRICTION_FIELDS;
                      return (
                        <TextField
                          key={field.key}
                          label={field.label}
                          type="number"
                          size="small"
                          value={r[field.key] ?? ''}
                          onChange={(e) => {
                            const v = e.target.value;
                            if (v !== '' && Number(v) < 0) return;
                            setSelectedInventory({
                              ...selectedInventory,
                              restrictions: { ...r, [field.key]: v },
                            });
                          }}
                          inputProps={{ min: 0, step: 1 }}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              backgroundColor: isDark
                                ? alpha('#fff', 0.03)
                                : alpha('#fff', 0.7),
                              '& fieldset': {
                                borderColor: alpha(THEME_COLOR, 0.35),
                              },
                              '&:hover fieldset': {
                                borderColor: THEME_COLOR,
                              },
                              '&.Mui-focused fieldset': {
                                borderColor: THEME_COLOR,
                              },
                            },
                          }}
                        />
                      );
                    })}
                  </Box>

                  {(selectedInventory.restrictions?.closeOnArrival ||
                    selectedInventory.restrictions?.closeOnDeparture) &&
                    selectedInventory.restriction_action === 'apply' && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: 'block', mt: 1.25 }}
                    >
                      Only the restrictions API is pushed — inventory counts are not zeroed.
                    </Typography>
                  )}
                </Box>
              </Grid>
            )}
            {/* Stop Sell — Apply / Clear (stopSell true / false) */}
            {selectedInventory.available === 'sold_out' && (
              <Grid item xs={12}>
                <Box
                  sx={{
                    borderRadius: 2,
                    border: `1px solid ${alpha(THEME_COLOR, 0.35)}`,
                    background: isDark
                      ? `linear-gradient(165deg, ${alpha(THEME_COLOR, 0.08)} 0%, ${alpha('#000', 0.2)} 100%)`
                      : `linear-gradient(165deg, ${alpha(THEME_COLOR, 0.1)} 0%, ${alpha(THEME_COLOR, 0.02)} 100%)`,
                    p: 1.5,
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 1,
                      flexWrap: 'wrap',
                      mb: 1,
                    }}
                  >
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                        Stop sell
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Apply pushes stopSell: true · Clear pushes stopSell: false
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 0.75 }}>
                      {[
                        { value: 'apply', label: 'Apply (Yes)' },
                        { value: 'clear', label: 'Clear (No)' },
                      ].map((opt) => {
                        const on = (selectedInventory.stop_sell_action || 'apply') === opt.value;
                        return (
                          <Chip
                            key={opt.value}
                            label={opt.label}
                            onClick={() =>
                              setSelectedInventory({
                                ...selectedInventory,
                                stop_sell_action: opt.value,
                              })
                            }
                            variant="outlined"
                            sx={{
                              cursor: 'pointer',
                              fontWeight: on ? 700 : 500,
                              backgroundColor: on ? THEME_COLOR : 'transparent',
                              color: on ? '#000' : 'inherit',
                              borderColor: on ? THEME_COLOR : alpha(THEME_COLOR, 0.45),
                              borderWidth: on ? 1.5 : 1,
                              '&:hover': {
                                backgroundColor: on ? THEME_COLOR : alpha(THEME_COLOR, 0.08),
                                borderColor: THEME_COLOR,
                              },
                            }}
                          />
                        );
                      })}
                    </Box>
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                    {(selectedInventory.stop_sell_action || 'apply') === 'apply'
                      ? 'Marks selected OTAs sold out and pushes stopSell to them.'
                      : 'Removes stop sell on selected OTAs (stopSell: false) and restores availability.'}
                  </Typography>
                </Box>
              </Grid>
            )}

            {/* Reason - After Block Type */}
            <Grid item xs={12}>
              <TextField
                label="Reason (Optional)"
                fullWidth
                size="small"
                multiline
                rows={2}
                value={selectedInventory.reason || ''}
                onChange={(e) => setSelectedInventory({ ...selectedInventory, reason: e.target.value })}
                placeholder="e.g., Maintenance, Event, etc."
              />
            </Grid>

            {/* Channel Selection — Restrictions / Stop Sell allow individual OTAs */}
            <Grid item xs={12}>
              <AllChannelsSelector
                channels={channels}
                value={selectedChannels}
                onChange={setSelectedChannels}
                allowPms={pmsChannelAllowed}
                pmsMode="toggle"
                otaMode={pmsChannelAllowed ? 'display' : 'selectable'}
                themeColor={THEME_COLOR}
                disabled={isSaving}
                helperText={
                  !hasChannels
                    ? (pmsChannelAllowed
                        ? 'No OTA channels configured. PMS is the only available target.'
                        : 'No OTA channels configured.')
                    : pmsChannelAllowed
                    ? 'Save syncs remaining daily inventory to all selected channels, matching the calendar for each date. PMS is optional.'
                    : 'All Channels is selected by default. Tap an OTA to include or exclude it. Save pushes restrictions or stop sell only to the selected OTAs.'
                }
                channelMessages={
                  channelUpdateMessages['all']
                    ? { all: channelUpdateMessages['all'] }
                    : null
                }
              />
            </Grid>
          </Grid>
          )}
        </Box>
        
        {/* Footer */}
        <Box
          sx={{
            p: 1.5,
            ...inventoryDrawerFooterSx(isDark),
            display: 'flex',
            gap: 1.5,
          }}
        >
          <Button
            variant="outlined"
            fullWidth
            onClick={closeUpdateInventoryDrawer}
            disabled={isSaving}
            sx={{
              borderColor: themeColors.surfaceBorder,
              color: themeColors.subtleText,
              '&:hover': {
                borderColor: THEME_COLOR,
                backgroundColor: isDark ? 'rgba(76, 225, 182, 0.1)' : 'rgba(76, 225, 182, 0.05)',
                color: THEME_COLOR,
              },
              '&:disabled': {
                borderColor: themeColors.surfaceBorder,
                color: isDark ? '#666' : '#999',
              },
            }}
          >
            Close
          </Button>
          <Button
            variant="contained"
            fullWidth
            onClick={inventoryAdjustMode ? handleSaveInventoryBlock : handleSaveInventory}
            disabled={isSaving || loading}
            sx={{
              backgroundColor: THEME_COLOR,
              color: '#000',
              fontWeight: 600,
              '&:hover': {
                backgroundColor: THEME_COLOR,
                opacity: 0.9,
              },
              '&:disabled': {
                backgroundColor: themeColors.surfaceBorder,
                color: isDark ? '#666' : '#999',
              },
            }}
          >
            {isSaving ? 'Saving...' : (inventoryAdjustMode ? (editingInventoryBlock ? 'Update Block' : 'Create Block') : 'Save Inventory')}
          </Button>
        </Box>
        <RateActionSuccessDialog
          open={Boolean(successDialog)}
          title={successDialog?.title}
          message={successDialog?.message}
          container={() => drawerContainerRef.current || undefined}
          onClose={closeUpdateInventoryDrawer}
        />
        </Box>
      </Drawer>

      {/* Delete Confirmation Dialog - Inside Drawer */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => {
          setDeleteDialogOpen(false);
          setBlockToDelete(null);
        }}
        container={drawerContainerRef.current || undefined}
        sx={{
          position: 'absolute',
          zIndex: 1400,
        }}
        PaperProps={{
          sx: {
            backgroundColor: isDark ? themeColors.surface : '#ffffff',
            borderRadius: 3,
            minWidth: 400,
            maxWidth: 500,
            margin: 2,
            position: 'absolute',
          },
        }}
        BackdropProps={{
          sx: {
            backgroundColor: isDark ? 'rgba(0, 0, 0, 0.7)' : 'rgba(0, 0, 0, 0.5)',
            position: 'absolute',
          },
        }}
      >
        <DialogTitle sx={{ color: themeColors.text }}>Delete Inventory Block</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: isDark ? '#999' : '#666' }}>
            Are you sure you want to delete the inventory block for "{blockToDelete?.room_type_name || `Room Type ${blockToDelete?.room_type_id}`}"? 
            This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => {
              setDeleteDialogOpen(false);
              setBlockToDelete(null);
            }}
            sx={{ color: themeColors.subtleText }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteInventoryBlock}
            color="error"
            variant="contained"
            disabled={isSaving}
          >
            {isSaving ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={Boolean(snackbarMessage)}
        autoHideDuration={4000}
        onClose={() => setSnackbarMessage(null)}
        message={snackbarMessage}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  );
};

export default ManageInventoryPage;

